import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { List, useDynamicRowHeight } from 'react-window';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '../../components/ui/command';
import { Textarea } from '../../components/ui/textarea';
import StagingActionsHelpPopover from '../../components/admin/StagingActionsHelpPopover';
import MiddleTruncate from '../../components/common/MiddleTruncate';
import { analyzeMergeOps } from './mergeGraphAnalyzer';
import MergePreviewModal from './bulkSimilarity/MergePreviewModal';
import ApplyReportModal from './bulkSimilarity/ApplyReportModal';
import { buildApplyPlan } from './bulkSimilarity/buildApplyPlan';
import { runApplyPlan, buildRetryInputs } from './bulkSimilarity/runApplyPlan';
import ChainLandingChip from './bulkSimilarity/ChainLandingChip';
import InboundMergeChip from './bulkSimilarity/InboundMergeChip';
import useBatchReviewLock from './bulkSimilarity/useBatchReviewLock';
import BatchReviewLockGate from './bulkSimilarity/BatchReviewLockGate';
import TopicPickerMulti from '../../components/shared/TopicPickerMulti';
import {
  Loader2, CheckCircle2, GitMerge, SkipForward, Clock, RefreshCw,
  Zap, AlertTriangle, Tag, UserCheck, Plus, Settings2, Eye,
  ArrowLeft, Keyboard, Pencil, X, Check, Send, ChevronDown, Palette,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NAUTICAL = ['Master', 'Chief Mate', '2MFG'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Bulk Similarity Page
 *
 * Per-batch triage workspace. Replaces the old in-modal experience with a
 * dedicated page so admins doing 20-30min review sessions have:
 *   • Bookmarkable URLs (threshold, filter, selected row all in querystring)
 *   • Sticky toolbar + sticky Apply bar
 *   • Master-detail layout — scan rows on the left, decide on the right
 *   • Keyboard shortcuts (K/M/S/D for actions, J/K for navigation, ? for help)
 *   • localStorage-persisted in-progress action state (survives accidental tab close)
 *
 * Endpoints called:
 *   GET  /admin/bulk-import/batch/{batchId}/bulk-similarity
 *   POST /admin/bulk-import/staging/bulk-delete       (skip)
 *   POST /admin/bulk-import/approve                   (keep — promote to live DB)
 *   POST /admin/questions/merge                        (staging → approved DB merge)
 *   POST /admin/bulk-import/staging/merge-into-staging (staging → staging cross-file merge)
 *   POST /admin/bulk-import/staging/bulk-edit          (Power-Ups)
 *   GET  /topics, GET /surveyors                       (Power-Ups dropdowns)
 */



const BulkSimilarityPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // iter 109: cooperative review lock — prevents the two-admins-on-the-
  // same-batch race where overlapping Apply calls could partial-insert
  // questions. `canMutate` is threaded down into the mutation surfaces
  // (Apply bar, per-row action buttons) via the `readOnly` flag below.
  const lock = useBatchReviewLock(batchId, token);
  const readOnly = !lock.canMutate;
  // "Take over" click needs a confirmation because it kicks the current
  // holder off in the middle of their session. useState + inline modal
  // is cheaper than pulling in another dialog primitive.
  const [confirmTakeOver, setConfirmTakeOver] = useState(false);

  // ---------- URL-backed state ----------
  const threshold = parseInt(searchParams.get('threshold') || '70', 10);
  // Local draft for the threshold input. The URL param (and therefore the
  // fetch + client-side filter) is intentionally decoupled from keystrokes
  // so typing "80" doesn't fire off a compute at "8", "80". The draft
  // commits to `threshold` only when the user clicks Recompute or presses
  // Enter. Keeps `threshold` in sync if it's changed externally (URL edit
  // / back-forward navigation).
  const [thresholdDraft, setThresholdDraft] = useState(String(threshold));
  useEffect(() => {
    setThresholdDraft(String(threshold));
  }, [threshold]);
  const simScope = (() => {
    const v = (searchParams.get('scope') || 'both').toLowerCase();
    return ['both', 'approved', 'staging'].includes(v) ? v : 'both';
  })();
  const keywordFilters = useMemo(
    () => (searchParams.get('filter') || '').split(',').map((t) => t.trim()).filter(Boolean),
    [searchParams],
  );
  const focusedRowId = searchParams.get('row') || null;
  const searchQuery = searchParams.get('q') || '';
  const filterMode = (searchParams.get('fmode') === 'or') ? 'or' : 'and';
  // `actionable` toggle hides rows that have ZERO matches at the current
  // threshold in the current scope (= already unique, nothing to triage).
  // Default OFF so admins see the full batch unless they opt in.
  const showActionableOnly = searchParams.get('actionable') === '1';
  // `mismatch` toggle hides everything except rows whose CSV surveyor
  // name couldn't be matched to a known surveyor (auto-fallback to
  // Unknown Internal / Unknown External at import time). Lets admins
  // batch-clean these rows before Apply.
  const showMismatchOnly = searchParams.get('mismatch') === '1';

  // Per-bucket visible cap (5 / 10 / 15 / 20). We ALWAYS fetch top_k=20
  // from the backend so changing this dropdown is instant client-side —
  // no re-fetch, no risk of losing a selected merge target that's stored
  // in rowState. The selected target auto-pins into the visible slice
  // even when its natural rank is below the cap.
  const VISIBLE_CAP_OPTIONS = [5, 10, 15, 20];
  const VISIBLE_CAP_LS_KEY = 'bulk-similarity-visible-cap-v1';
  const [visibleCap, setVisibleCap] = useState(() => {
    try {
      const raw = parseInt(sessionStorage.getItem(VISIBLE_CAP_LS_KEY) || '5', 10);
      return VISIBLE_CAP_OPTIONS.includes(raw) ? raw : 5;
    } catch {
      return 5;
    }
  });
  useEffect(() => {
    try { sessionStorage.setItem(VISIBLE_CAP_LS_KEY, String(visibleCap)); } catch { /* quota */ }
  }, [visibleCap]);
  // `inboundTo` URL param overlays a focused filter: show ONLY the target row
  // and all rows that ultimately merge into it (direct + transitive). Powered
  // by the inbound-chip click on a target row.
  const inboundToId = searchParams.get('inboundTo') || null;

  const toggleActionableOnly = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get('actionable') === '1') next.delete('actionable');
      else next.set('actionable', '1');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const toggleMismatchOnly = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get('mismatch') === '1') next.delete('mismatch');
      else next.set('mismatch', '1');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const toggleFilterMode = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if ((next.get('fmode') === 'or')) next.delete('fmode');
      else next.set('fmode', 'or');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const toggleKeyword = useCallback((kw) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const current = (next.get('filter') || '').split(',').map((t) => t.trim()).filter(Boolean);
      const updated = current.includes(kw)
        ? current.filter((x) => x !== kw)
        : [...current, kw];
      if (updated.length === 0) next.delete('filter');
      else next.set('filter', updated.join(','));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearKeywordFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('filter');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Local input mirror (debounced into URL so dragging doesn't churn history)
  const [searchInput, setSearchInput] = useState(searchQuery);
  useEffect(() => { setSearchInput(searchQuery); }, [searchQuery]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('q', searchInput);
          else next.delete('q');
          return next;
        }, { replace: true });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [searchInput, searchQuery, setSearchParams]);

  const setUrlParam = useCallback((key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // ---------- Server state ----------
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [applying, setApplying] = useState(false);
  // Apply preview — populated when admin clicks Apply and the merge graph
  // contains either a CYCLE (admin must fix) or a TRANSITIVE CHAIN
  // (admin should confirm the eventual landing). null = no preview open.
  const [applyPreview, setApplyPreview] = useState(null);
  const [applyReport, setApplyReport] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [batchScope, setBatchScope] = useState(null);  // {grade, mmd, function_id, function_name, filename}

  // Per-row admin decisions
  const [rowState, setRowState] = useState({});  // staging_id -> { action, mergeTargetId, mergeTargetSource }

  // Multi-select state — admins can pick a subset of rows and apply
  // Keep/Skip/Defer (Merge is per-row only) or run a Power-Up against
  // just the selection instead of the entire filter. Persisted to
  // localStorage so refreshes / accidental tab close don't lose intent.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Index of last clicked row (within filteredRows). Used for Shift+click range.
  const lastClickedIndexRef = useRef(null);

  // Power-Ups
  const [topics, setTopics] = useState([]);
  const [scopeSurveyors, setScopeSurveyors] = useState([]);
  const [powerupBusy, setPowerupBusy] = useState(false);

  // Virtualized list ref + dynamic row-height cache (measures actual rendered
  // heights so there are no gaps from over-estimated predictions).
  const listRef = useRef(null);
  const rowHeight = useDynamicRowHeight({ defaultRowHeight: 220 });
  // Mirror of filteredRows for use inside callbacks (avoids stale closures
  // in shift-range selection + select-all-filtered handlers).
  const filteredRowsRef = useRef([]);

  // Live merge-graph analysis: recomputed only when rowState changes, NOT on
  // every render. Used to show inline chain landings (e.g. "Lands in: Row 20")
  // on each row card *as the admin decides* — proactive visibility instead
  // of waiting until Apply to reveal the chain via the modal. Pure JS, no
  // server calls, <1ms for realistic batch sizes.
  const liveAnalysis = useMemo(() => analyzeMergeOps(rowState), [rowState]);

  // staging_id -> { kind: 'cycle'|'chain', finalTarget, finalTargetSource, hops }
  // hops includes intermediate steps so we can render "→ Row 20 → Q_approved".
  const chainHintByRow = useMemo(() => {
    const map = {};
    for (const cycle of liveAnalysis.cycles) {
      for (const sid of cycle) {
        map[sid] = { kind: 'cycle' };
      }
    }
    for (const chain of liveAnalysis.chains) {
      map[chain.source] = {
        kind: 'chain',
        finalTarget: chain.finalTarget,
        finalTargetSource: chain.finalTargetSource,
        hops: chain.path.length - 1,
      };
    }
    return map;
  }, [liveAnalysis]);

  // staging_id -> { count, sources: [stagingId,...] }  (only for staging targets)
  // Counts every row that ultimately lands in this target (direct + transitive).
  // Powers the back-reference chip on target rows ("← N rows merge here").
  const inboundByRow = useMemo(() => {
    const map = {};
    if (liveAnalysis.hasErrors) return map;
    const sourceToUltimate = new Map();
    const chainSources = new Set(liveAnalysis.chains.map((c) => c.source));
    for (const ch of liveAnalysis.chains) {
      sourceToUltimate.set(ch.source, { target: ch.finalTarget, kind: ch.finalTargetSource });
    }
    for (const op of liveAnalysis.orderedOps) {
      if (chainSources.has(op.source_staging_id)) continue;
      sourceToUltimate.set(op.source_staging_id, { target: op.target_staging_id, kind: 'staging' });
    }
    for (const op of liveAnalysis.approvedOps) {
      if (!sourceToUltimate.has(op.source)) {
        sourceToUltimate.set(op.source, { target: op.target, kind: 'approved' });
      }
    }
    for (const [, { target, kind }] of sourceToUltimate) {
      if (kind !== 'staging') continue;
      if (!map[target]) map[target] = { count: 0, sources: [] };
      map[target].count++;
    }
    // Second pass to fill sources arrays (needed after counts are stable)
    for (const [source, { target, kind }] of sourceToUltimate) {
      if (kind !== 'staging') continue;
      map[target].sources.push(source);
    }
    return map;
  }, [liveAnalysis]);

  // Form defaults for each Power-Up popover
  const buildDefaults = useCallback(() => {
    const now = new Date();
    return {
      topic: { topic_ids: [], mode: (typeof localStorage !== 'undefined' && localStorage.getItem('bulk-topic-mode-v1')) || 'add' },
      surveyor: { asked_by_internal: false, surveyor_id: '' },
      sitting: {
        asked_by_internal: false,
        surveyor_id: '',
        exam_month: MONTHS[now.getMonth()],
        exam_year: String(now.getFullYear()),
        asked: 1,
      },
      count: { asked: 1 },
    };
  }, []);
  const _defaults = useMemo(() => buildDefaults(), [buildDefaults]);
  const [topicForm, setTopicForm] = useState(_defaults.topic);
  const [surveyorForm, setSurveyorForm] = useState(_defaults.surveyor);
  const [sittingForm, setSittingForm] = useState(_defaults.sitting);
  const [countForm, setCountForm] = useState(_defaults.count);

  // ---------- Persistence key for in-progress decisions ----------
  // Persist rowState per batch. Bump the schema tag when the default
  // action semantics change so stale persisted state doesn't override the
  // new safe defaults. v2 (2026-02) migrated the untouched-row default
  // from 'keep' → 'defer' to fix the silent staging-target auto-approval
  // bug. If an older payload is found, we discard it — an admin may have
  // to re-tag a small number of in-progress rows, but that's much safer
  // than replaying pre-fix 'keep' defaults.
  const lsKey = `bulkSim:v2:${batchId}:rowState`;
  const lsSelKey = `bulkSim:${batchId}:selectedIds`;

  // Hydrate selection from localStorage on mount (per-batch)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(lsSelKey);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length) setSelectedIds(new Set(arr));
      }
    } catch { /* quota */ }
  }, [lsSelKey]);

  // Persist selection on every change
  useEffect(() => {
    try {
      if (selectedIds.size === 0) localStorage.removeItem(lsSelKey);
      else localStorage.setItem(lsSelKey, JSON.stringify(Array.from(selectedIds)));
    } catch { /* quota */ }
  }, [selectedIds, lsSelKey]);

  // ---------- Data fetch ----------
  const fetchSimilarity = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/bulk-import/batch/${batchId}/bulk-similarity`,
        null,
        // Always fetch the max slice (top_k=20). The visibleCap dropdown
        // then trims client-side without a network round-trip, so a
        // selected merge target never falls off the API response.
        { headers, params: { threshold: threshold / 100, top_k: 20 } },
      );
      setPayload(res.data);

      // Hydrate scope from the first staging question (all share same scope per batch)
      const firstResult = Object.values(res.data?.results || {})[0];
      if (firstResult && !batchScope) {
        // Need to fetch full row to get scope — call the staging list and filter
        try {
          const stRes = await axios.get(`${API}/admin/bulk-import/staging`, { headers });
          const fg = (stRes.data?.file_groups || []).find((g) => g.import_batch_id === batchId);
          if (fg) {
            setBatchScope({
              grade: fg.grade,
              mmd_location: fg.mmd_location,
              function_id: fg.function_id,
              function_name: fg.function_name,
              filename: fg.filename,
              questionCount: fg.questions.length,
            });
          }
        } catch { /* non-fatal */ }
      }

      // Initialize/merge row state — preserve any in-progress decisions
      const persisted = (() => {
        try { return JSON.parse(localStorage.getItem(lsKey) || '{}'); } catch { return {}; }
      })();
      const init = {};
      Object.keys(res.data?.results || {}).forEach((sid) => {
        // Default action is 'defer' (no-op) — NOT 'keep'. Keep is an
        // AFFIRMATIVE action that promotes the row to the approved DB
        // on Apply. Historical bug: defaulting to 'keep' caused any
        // staging row an admin used as a MERGE TARGET (but didn't
        // explicitly action) to auto-promote to approved when Apply
        // ran the keeps step BEFORE the staging→staging merges,
        // silently 404-ing the merges and dropping the target out of
        // staging into the approved DB. Regression test:
        // src/pages/admin/__tests__/analyzeMergeOps.test.js
        init[sid] = persisted[sid] || { action: 'defer', mergeTargetId: null, mergeTargetSource: null };
      });
      setRowState(init);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to compute bulk similarity');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [batchId, threshold, headers, batchScope, lsKey]);

  useEffect(() => {
    fetchSimilarity();
  }, [batchId, fetchSimilarity]);

  // Persist rowState to localStorage so accidental tab-close doesn't lose 30min of work
  useEffect(() => {
    if (Object.keys(rowState).length > 0) {
      try { localStorage.setItem(lsKey, JSON.stringify(rowState)); } catch { /* quota */ }
    }
  }, [rowState, lsKey]);

  // Fetch scope-aware Power-Up dropdowns
  useEffect(() => {
    if (!batchScope?.grade || !batchScope?.function_id) return;
    const isNautical = NAUTICAL.includes(batchScope.grade);
    (async () => {
      try {
        const [tRes, sRes] = await Promise.all([
          axios.get(`${API}/topics`, { headers, params: { grade: batchScope.grade, function_id: batchScope.function_id } }),
          axios.get(`${API}/surveyors`, { headers }),
        ]);
        setTopics(tRes.data || []);
        const filtered = (sRes.data || []).filter((s) => {
          if (s.is_active === false) return false;
          if (!s.grades?.includes(batchScope.grade)) return false;
          if (isNautical) return true;
          const alt = batchScope.mmd_location?.endsWith(' MMD') ? batchScope.mmd_location : `${batchScope.mmd_location} MMD`;
          return s.mmd_locations?.includes(batchScope.mmd_location) || s.mmd_locations?.includes(alt);
        });
        setScopeSurveyors(filtered);
      } catch { /* non-blocking */ }
    })();
  }, [batchScope?.grade, batchScope?.mmd_location, batchScope?.function_id, headers]);

  // ---------- Derived state ----------
  const allRows = useMemo(() => {
    // Ensure similar-question arrays are always sorted DESCENDING by
    // similarity — highest match at index 0. Two reasons:
    //   1. UI expects the top card to be the most-similar match.
    //   2. Downstream merge-target pickers use `[0]` / `.find(c => c.similarity >= threshold)`
    //      and pick a suboptimal target when the backend order isn't stable.
    // Backend does NOT guarantee this ordering, so we normalize once here.
    const rows = Object.values(payload?.results || {});
    return rows.map((r) => ({
      ...r,
      approved_similar: [...(r.approved_similar || [])].sort(
        (a, b) => (b.similarity ?? 0) - (a.similarity ?? 0),
      ),
      staging_similar: [...(r.staging_similar || [])].sort(
        (a, b) => (b.similarity ?? 0) - (a.similarity ?? 0),
      ),
    }));
  }, [payload]);

  const allKeywords = useMemo(() => {
    const map = new Map();
    for (const r of allRows) {
      const tags = (r.keywords || '').split(';').map((t) => t.trim()).filter(Boolean);
      for (const t of tags) map.set(t, (map.get(t) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [allRows]);

  const preInboundRows = useMemo(() => {
    // Helper: apply threshold filter, cap to visibleCap, then AUTO-PIN the
    // currently-selected merge target so it stays on screen even when the
    // admin dips the cap below the target's natural rank. The pin is
    // additive — the visible list becomes `cap` best matches + 1 pinned
    // outsider (marked `_pinned: true` for the badge).
    const sliceWithPin = (arr, sid, source) => {
      const filtered = arr.filter((c) => c.similarity >= threshold);
      const sliced = filtered.slice(0, visibleCap);
      const st = rowState[sid];
      if (!st || st.action !== 'merge' || !st.mergeTargetId) return sliced;
      if (st.mergeTargetSource && st.mergeTargetSource !== source) return sliced;
      const alreadyIn = sliced.some((m) => m.id === st.mergeTargetId);
      if (alreadyIn) return sliced;
      const rank = filtered.findIndex((m) => m.id === st.mergeTargetId);
      if (rank < 0) return sliced;  // target not in this bucket (or below threshold)
      return [...sliced, { ...filtered[rank], _pinned: true, _rank: rank + 1 }];
    };

    let rows = allRows.map((r) => ({
      ...r,
      // Honor sim-scope toggle: empty whichever side the admin hid.
      approved_similar_filtered:
        simScope === 'staging' ? [] : sliceWithPin(r.approved_similar, r.staging_question_id, 'approved'),
      staging_similar_filtered:
        simScope === 'approved' ? [] : sliceWithPin(r.staging_similar, r.staging_question_id, 'staging'),
    }));
    if (keywordFilters.length > 0) {
      // AND = row must contain EVERY selected keyword (narrow)
      // OR  = row matches ANY selected keyword (broad)
      rows = rows.filter((r) => {
        const tags = (r.keywords || '').split(';').map((t) => t.trim()).filter(Boolean);
        return filterMode === 'or'
          ? keywordFilters.some((kw) => tags.includes(kw))
          : keywordFilters.every((kw) => tags.includes(kw));
      });
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter((r) => (r.question_text || '').toLowerCase().includes(q));
    }
    if (showActionableOnly) {
      // Only keep rows that have at least one match >= threshold in the
      // current scope (counting both approved and staging similar lists
      // after the simScope toggle has already been applied above).
      rows = rows.filter((r) =>
        (r.approved_similar_filtered.length + r.staging_similar_filtered.length) > 0,
      );
    }
    if (showMismatchOnly) {
      // Only keep rows whose CSV surveyor name didn't resolve at import
      // time and were fallback-assigned to Unknown Internal / External.
      rows = rows.filter((r) => r.surveyor_mismatch === true);
    }
    return rows;
    // NOTE: `inboundByRow` used to be a dep here (for the inboundTo
    // filter), but that made this memo regenerate on every
    // Keep/Merge/Skip/Defer click — which reset react-window's scroll
    // to 0 (the "auto-scrolls to top" bug). The inbound filter is now
    // applied in a separate memo below and only pays the recompute cost
    // when `inboundToId` is actually set.
  }, [allRows, threshold, simScope, keywordFilters, searchQuery, filterMode, showActionableOnly, showMismatchOnly, visibleCap, rowState]);

  // Final filtered list — applies the optional inbound-focused narrowing
  // on top of the main filter pipeline. Downstream code keeps using
  // `filteredRows`; the split above is purely to keep this memo's
  // identity stable when `inboundToId` is null (which is the default).
  const filteredRows = useMemo(() => {
    if (!inboundToId || !inboundByRow[inboundToId]) return preInboundRows;
    const allow = new Set([inboundToId, ...inboundByRow[inboundToId].sources]);
    return preInboundRows.filter((r) => allow.has(r.staging_question_id));
  }, [preInboundRows, inboundToId, inboundByRow]);

  // Keep ref in sync so toggleSelect's range branch can read the latest list.
  useEffect(() => { filteredRowsRef.current = filteredRows; }, [filteredRows]);

  // "Select all in current filter" — toggles between empty and the full set
  // of currently filtered row IDs. Useful when a keyword chip narrows the
  // list to a meaningful subset.
  const selectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const filteredIdSet = new Set(filteredRowsRef.current.map((r) => r.staging_question_id));
      // If every filtered row is already selected → clear them; else add them all.
      const allAlreadyIn = filteredRowsRef.current.length > 0
        && filteredRowsRef.current.every((r) => prev.has(r.staging_question_id));
      if (allAlreadyIn) {
        const next = new Set(prev);
        filteredIdSet.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      filteredIdSet.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  // Auto-focus the first row when filter changes and current focus is no longer in the filtered list
  useEffect(() => {
    if (filteredRows.length === 0) return;
    if (!focusedRowId || !filteredRows.find((r) => r.staging_question_id === focusedRowId)) {
      setUrlParam('row', filteredRows[0].staging_question_id);
    }
  }, [filteredRows, focusedRowId, setUrlParam]);

  // ---------- Jump-to-row helper (for ChainLandingChip clicks) ----------
  // Sets URL focus → existing useEffect smooth-scrolls to the row → also
  // briefly pulses the destination so admin can spot it after the scroll.
  const [pulsingRowId, setPulsingRowId] = useState(null);
  const jumpToRow = useCallback((stagingId) => {
    if (!stagingId) return;
    setUrlParam('row', stagingId);
    setPulsingRowId(stagingId);
    setTimeout(() => setPulsingRowId((cur) => (cur === stagingId ? null : cur)), 1400);
  }, [setUrlParam]);

  // Scroll focused row into view (centered) whenever focus MOVES — either
  // via keyboard nav or an explicit URL landing (?row=…). Deliberately
  // does NOT depend on `filteredRows`: that reference regenerates on
  // every `rowState` change (via the liveAnalysis → inboundByRow →
  // filteredRows chain), which would fire this effect on every
  // Keep/Merge/Skip/Defer click and yank the list back to the focused
  // row's position — a reported "auto-scrolls to top" bug when the
  // auto-focused first row was off-screen.
  //
  // We track the last-scrolled id in a ref so a genuine focus change
  // (keyboard j/k, URL edit) still scrolls, but action clicks don't.
  const lastScrolledFocusIdRef = useRef(null);
  useEffect(() => {
    if (!focusedRowId) return;
    if (lastScrolledFocusIdRef.current === focusedRowId) return;
    const idx = filteredRows.findIndex((r) => r.staging_question_id === focusedRowId);
    if (idx >= 0 && listRef.current) {
      try {
        listRef.current.scrollToRow({ index: idx, align: 'smart', behavior: 'smooth' });
      } catch { /* ref not ready yet */ }
    }
    lastScrolledFocusIdRef.current = focusedRowId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedRowId]);

  // One-shot pulse on initial mount when we land with ?row=<id>. This
  // catches cross-batch new-tab landings from another triage page's
  // Row N link — the row is already focused via focusedRowId, but a
  // brief amber pulse makes it obvious *why* the tab opened.
  //
  // Guard 1: only fire once per mount (initialPulseFiredRef).
  // Guard 2: only fire for a row that was EXPLICITLY in the URL at page
  //   load. Otherwise the auto-first-row effect (which writes
  //   ?row=<first> right after mount) would trip the pulse on every
  //   visit — that was a real bug reported by the user.
  const initialPulseFiredRef = useRef(false);
  // Snapshot the ?row= value from the very first render. useState with
  // a lazy initializer runs exactly once, before any effects, so the
  // auto-first-row setter can't overwrite this snapshot.
  const [initialUrlRowId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('row') || null;
    } catch { return null; }
  });
  useEffect(() => {
    if (initialPulseFiredRef.current) return;
    if (!initialUrlRowId) return;               // Guard 2: only deep-links
    if (filteredRows.length === 0) return;
    const exists = filteredRows.some((r) => r.staging_question_id === initialUrlRowId);
    if (!exists) return;
    initialPulseFiredRef.current = true;
    setPulsingRowId(initialUrlRowId);
    const t = setTimeout(
      () => setPulsingRowId((cur) => (cur === initialUrlRowId ? null : cur)),
      1400,
    );
    return () => clearTimeout(t);
  }, [initialUrlRowId, filteredRows]);

  const focusedRow = useMemo(
    () => filteredRows.find((r) => r.staging_question_id === focusedRowId) || filteredRows[0],
    [filteredRows, focusedRowId],
  );

  const summary = useMemo(() => {
    let keep = 0, merge = 0, skip = 0, defer = 0;
    for (const r of allRows) {
      // NOTE: untagged rows default to 'defer' (not 'keep') because
      // Keep is now an AFFIRMATIVE action that promotes a staging row
      // straight to the live questions DB on Apply. Untagged =
      // "not yet decided" = same DB outcome as Defer (no-op).
      const a = rowState[r.staging_question_id]?.action || 'defer';
      if (a === 'merge') merge++;
      else if (a === 'skip') skip++;
      else if (a === 'keep') keep++;
      else defer++;
    }
    return { keep, merge, skip, defer };
  }, [allRows, rowState]);

  // ---------- Action helpers ----------
  const setAction = useCallback((stagingId, action, mergeTargetId = null, mergeTargetSource = null) => {
    setRowState((prev) => ({ ...prev, [stagingId]: { action, mergeTargetId, mergeTargetSource } }));
  }, []);

  // ---------- Selection helpers ----------
  // Click semantics follow the standard Gmail / Excel / Finder model so the
  // interaction is predictable for power users:
  //   • plain click       → select ONLY this row (clear others); anchor = this row
  //   • Ctrl/Cmd+click    → toggle this row, preserve others; anchor = this row
  //   • Shift+click       → select range from anchor → this row, REPLACING
  //                         the current selection. If no anchor exists yet,
  //                         falls back to plain-click behaviour.
  // The "anchor" is the last index reached via plain or Ctrl/Cmd click.
  const toggleSelect = useCallback((stagingId, index, mode = 'single') => {
    setSelectedIds((prev) => {
      // Shift+range — anchor must already exist
      if (mode === 'range' && lastClickedIndexRef.current !== null) {
        const start = Math.min(lastClickedIndexRef.current, index);
        const end = Math.max(lastClickedIndexRef.current, index);
        const list = filteredRowsRef.current || [];
        const next = new Set();
        for (let i = start; i <= end; i += 1) {
          const id = list[i]?.staging_question_id;
          if (id) next.add(id);
        }
        return next;
      }
      // Ctrl/Cmd+toggle — preserve existing, toggle this one
      if (mode === 'multi') {
        const next = new Set(prev);
        if (next.has(stagingId)) next.delete(stagingId);
        else next.add(stagingId);
        return next;
      }
      // Plain click — select only this row; if it was the lone selection,
      // toggle it off (so plain-click on an already-singly-selected row clears).
      if (prev.size === 1 && prev.has(stagingId)) {
        return new Set();
      }
      return new Set([stagingId]);
    });
    // Anchor updates on plain + multi clicks only; range clicks keep anchor.
    if (mode !== 'range') lastClickedIndexRef.current = index;
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastClickedIndexRef.current = null;
  }, []);

  // Bulk-set action for every currently selected row. Used by the
  // selection toolbar. Merge is disabled when N>1 so we never reach
  // this with action='merge' on >1 row (UI gates it).
  const bulkSetAction = useCallback((action) => {
    if (selectedIds.size === 0) return;
    setRowState((prev) => {
      const next = { ...prev };
      selectedIds.forEach((sid) => {
        next[sid] = { action, mergeTargetId: null, mergeTargetSource: null };
      });
      return next;
    });
    toast.success(`${action[0].toUpperCase()}${action.slice(1)} marked on ${selectedIds.size} row(s)`);
  }, [selectedIds]);

  // Bulk Approve to DB — fast path for clean batches that don't need
  // per-row review. POSTs directly to /admin/bulk-import/approve which
  // moves selected staging rows to the live questions DB, computes
  // embeddings, and removes them from staging. Two-step confirm.
  const [bulkApproving, setBulkApproving] = useState(false);
  const handleBulkApprove = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    const ok = window.confirm(
      `Approve ${n} selected row${n === 1 ? '' : 's'} directly to the live questions DB?\n\n`
      + 'This pushes them to the approved questions table and removes them from staging. '
      + 'This cannot be undone.',
    );
    if (!ok) return;
    setBulkApproving(true);
    try {
      const ids = Array.from(selectedIds);
      const res = await axios.post(
        `${API}/admin/bulk-import/approve`,
        { staging_ids: ids },
        { headers },
      );
      const approved = res.data?.approved_count || 0;
      const errs = (res.data?.errors || []).length;
      if (approved > 0) {
        toast.success(`Approved ${approved} row(s)${errs ? `, ${errs} failed` : ''}`);
      } else {
        toast.error(`Approval failed: ${errs ? `${errs} error(s)` : 'no rows approved'}`);
      }
      // Clear the now-stale local state and selection
      setRowState((prev) => {
        const next = { ...prev };
        ids.forEach((sid) => delete next[sid]);
        return next;
      });
      clearSelection();
      await fetchSimilarity(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setBulkApproving(false);
    }
  }, [selectedIds, headers, clearSelection, fetchSimilarity]);

  const moveFocus = useCallback((delta) => {
    if (filteredRows.length === 0) return;
    const idx = filteredRows.findIndex((r) => r.staging_question_id === focusedRow?.staging_question_id);
    const next = filteredRows[(idx + delta + filteredRows.length) % filteredRows.length];
    setUrlParam('row', next.staging_question_id);
  }, [filteredRows, focusedRow, setUrlParam]);

  // ---------- Keyboard shortcuts ----------
  useEffect(() => {
    const onKeyDown = (e) => {
      // Skip when typing in inputs/textareas/selects
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (!focusedRow) return;
      const fid = focusedRow.staging_question_id;
      // ALL staging matches are mergeable — backend enforces scope; no
      // need to filter by same-batch/same-file on the client.
      const hasMerge = focusedRow.approved_similar_filtered.length > 0
        || focusedRow.staging_similar_filtered.length > 0;
      switch (k) {
        case 'k': setAction(fid, 'keep'); e.preventDefault(); break;
        case 's': setAction(fid, 'skip'); e.preventDefault(); break;
        case 'd': setAction(fid, 'defer'); e.preventDefault(); break;
        case 'm':
          if (hasMerge) {
            const tgt = focusedRow.approved_similar_filtered[0]
              || focusedRow.staging_similar_filtered[0];
            if (tgt) {
              const src = tgt.source === 'approved' ? 'approved' : 'staging';
              setAction(fid, 'merge', tgt.id, src);
            }
          }
          e.preventDefault();
          break;
        case 'j': moveFocus(1); e.preventDefault(); break;
        case 'arrowdown': moveFocus(1); e.preventDefault(); break;
        case 'arrowup': moveFocus(-1); e.preventDefault(); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focusedRow, setAction, moveFocus]);

  // ---------- Apply ----------
  // Step 1: gather raw decisions
  // Step 2: analyze the merge graph for cycles + transitive chains
  // Step 3: if cycles → block via preview modal, admin must fix
  //          if chains → confirm via preview modal so admin sees the landing
  //          else → execute immediately
  // Step 4 (executeApply): run skips, then staging→staging merges in
  //          topological order, then staging→approved merges.
  // Every Apply click now opens the Preview modal — the modal renders
  // the full 4-step plan (Merge → Skip → Keep) in execution order and
  // surfaces cross-reference warnings (target-also-keep / target-also-skip)
  // that used to silently misfire. Confirm & Apply in the modal is what
  // actually triggers executeApply. Cycles disable the Confirm button.
  const handleApply = () => {
    const skipIds = [];
    const keepIds = [];
    for (const sid in rowState) {
      if (rowState[sid].action === 'skip') skipIds.push(sid);
      else if (rowState[sid].action === 'keep') keepIds.push(sid);
    }
    const analysis = analyzeMergeOps(rowState);

    // Nothing-to-apply is now surfaced INSIDE the modal (empty state)
    // rather than via a toast — admins get one consistent surface. The
    // modal still opens on cycles even when everything else is empty,
    // so we don't short-circuit here.
    setApplyPreview({ skipIds, keepIds, analysis });
  };

  // Actually run the merge ops. Called either directly from handleApply
  // (when there are no chains/cycles) or from the preview modal's
  // "Confirm & Apply" button (when admin has acknowledged the chain summary).
  //
  // Delegates to runApplyPlan (pure executor) — this component's job is
  // just to feed it inputs, capture the ApplyReport, and hand it to the
  // ApplyReportModal for admin review. See runApplyPlan.js for the exact
  // 4-step execution order and the historical context that shaped it.
  const executeApply = async (skipIds, keepIds, analysis) => {
    setApplyPreview(null);
    setApplying(true);
    try {
      const plan = buildApplyPlan({
        skipIds, keepIds, analysis, rowState, allRows,
      });
      const report = await runApplyPlan({
        plan,
        skipIds,
        keepIds,
        analysis,
        batchId,
        deps: { axios, headers, API },
      });
      setApplyReport(report);
      // We deliberately do NOT clear localStorage here — a partial-failure
      // needs the admin's row-tags preserved for the Retry flow inside
      // ApplyReportModal. Cleanup happens on modal Close.
    } finally {
      setApplying(false);
    }
  };

  // Retry only the failed & retryable ops from the current report.
  // Runs through the SAME runApplyPlan pipeline; results merge back into
  // the existing report (succeeded ops replace their failed predecessors).
  const retryFailedOps = async () => {
    if (!applyReport) return;
    const retryInputs = buildRetryInputs(applyReport);
    const hasRetryable =
      retryInputs.skipIds.length +
        retryInputs.keepIds.length +
        retryInputs.analysis.orderedOps.length +
        retryInputs.analysis.approvedOps.length >
      0;
    if (!hasRetryable) return;
    setRetrying(true);
    try {
      const retryPlan = buildApplyPlan({
        ...retryInputs, rowState, allRows,
      });
      const retryReport = await runApplyPlan({
        plan: retryPlan,
        ...retryInputs,
        batchId,
        deps: { axios, headers, API },
      });
      // Merge: succeeded ops in retryReport overwrite matching failures
      // in the prior report. Anything that failed a second time replaces
      // the earlier failure entry too (fresh error detail).
      const idxOf = (r) => {
        if (r.step === 'merge-staging' || r.step === 'merge-approved') {
          return `${r.step}:${r.source.id}->${r.target.id}`;
        }
        return `${r.step}:${r.row.id}`;
      };
      const retryByKey = new Map();
      for (const r of retryReport.results) retryByKey.set(idxOf(r), r);
      const merged = applyReport.results.map((r) => {
        const k = idxOf(r);
        return retryByKey.has(k) ? retryByKey.get(k) : r;
      });
      const okCount = merged.filter((r) => r.ok).length;
      setApplyReport({
        ...applyReport,
        results: merged,
        totals: {
          ok: okCount,
          failed: merged.length - okCount,
          total: merged.length,
        },
        timing: {
          ...applyReport.timing,
          endedAt: retryReport.timing.endedAt,
          durationMs:
            (applyReport.timing.durationMs || 0) + retryReport.timing.durationMs,
        },
      });
    } finally {
      setRetrying(false);
    }
  };

  const closeApplyReport = () => {
    // If everything succeeded, clear the persisted row-tags and go back
    // to the bulk-import review list. If failures remain, leave state
    // intact so the admin can inspect the page and re-attempt manually.
    if (applyReport && applyReport.totals.failed === 0) {
      try { localStorage.removeItem(lsKey); } catch { /* */ }
      setApplyReport(null);
      navigate('/admin/bulk-import-review');
    } else {
      setApplyReport(null);
      // Refresh the page's similarity data so the admin sees post-apply state
      fetchSimilarity();
    }
  };

  // ---------- Power-Up handlers ----------
  const filteredIds = useMemo(() => filteredRows.map((r) => r.staging_question_id), [filteredRows]);

  // Power-Ups (and any bulk metadata edit) prefer the explicit selection
  // when admins have picked specific rows; otherwise they fall back to the
  // current filter so the legacy "operate on all visible rows" behavior
  // still works for admins who haven't engaged multi-select.
  const targetIds = useMemo(
    () => (selectedIds.size > 0 ? Array.from(selectedIds) : filteredIds),
    [selectedIds, filteredIds],
  );
  const targetMode = selectedIds.size > 0 ? 'selection' : 'filter';

  const callBulkEdit = async (extraBody, onSuccess) => {
    if (targetIds.length === 0) {
      toast.error('No staging rows in current selection/filter');
      return;
    }
    setPowerupBusy(true);
    try {
      const res = await axios.post(`${API}/admin/bulk-import/staging/bulk-edit`,
        { staging_ids: targetIds, ...extraBody }, { headers });
      toast.success(res.data?.message || `Updated ${targetIds.length} row(s)`);
      await fetchSimilarity(true);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk edit failed');
    } finally {
      setPowerupBusy(false);
    }
  };

  // Preserve internal-list scroll offset across re-renders that are
  // ONLY caused by rowState churn (Keep/Merge/Skip/Defer clicks). Without
  // this, every action click resets the react-window scroller to 0 —
  // reported as "auto-scrolls to top" by admins. We snapshot scrollTop on
  // click and re-apply it for the next few animation frames so any
  // async layout pass from react-window's dynamic row-height measurer
  // doesn't undo the restore.
  const listScrollerRef = useRef(null);
  const findListScroller = useCallback(() => {
    if (listScrollerRef.current && document.body.contains(listScrollerRef.current)) {
      return listScrollerRef.current;
    }
    const root = document.querySelector('[data-testid="rows-flat-list"]');
    if (!root) return null;
    const walk = (el) => {
      const s = window.getComputedStyle(el);
      if ((s.overflowY === 'auto' || s.overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      for (const c of el.children) { const f = walk(c); if (f) return f; }
      return null;
    };
    const found = walk(root);
    listScrollerRef.current = found;
    return found;
  }, []);
  const restoreLoopRef = useRef(null);
  const restoreScroll = useCallback((target) => {
    if (target == null) return;
    // Cancel any in-flight restore loop before starting a new one.
    if (restoreLoopRef.current) {
      cancelAnimationFrame(restoreLoopRef.current);
      restoreLoopRef.current = null;
    }
    let frames = 0;
    const tick = () => {
      const scroller = findListScroller();
      if (scroller && scroller.scrollTop < target - 4) {
        scroller.scrollTop = target;
      }
      frames += 1;
      // ~6 frames covers react-window's async dynamic-row measurement pass.
      if (frames < 6) {
        restoreLoopRef.current = requestAnimationFrame(tick);
      } else {
        restoreLoopRef.current = null;
      }
    };
    restoreLoopRef.current = requestAnimationFrame(tick);
  }, [findListScroller]);
  const captureAndRestoreScroll = useCallback(() => {
    const scroller = findListScroller();
    if (!scroller) return;
    const target = scroller.scrollTop;
    // Kick off the restore loop AFTER this microtask so the state update
    // + render fires first. The loop then no-ops until react-window
    // actually resets scrollTop to 0.
    Promise.resolve().then(() => restoreScroll(target));
  }, [findListScroller, restoreScroll]);

  // Wrap setAction so we snapshot the scrollTop before firing state.
  const setActionPreservingScroll = useCallback((stagingId, action, mergeTargetId = null, mergeTargetSource = null) => {
    captureAndRestoreScroll();
    setAction(stagingId, action, mergeTargetId, mergeTargetSource);
  }, [captureAndRestoreScroll, setAction]);

  const internalSurveyors = useMemo(() => scopeSurveyors.filter((s) => s.type === 'internal'), [scopeSurveyors]);
  const externalSurveyors = useMemo(() => scopeSurveyors.filter((s) => s.type === 'external'), [scopeSurveyors]);

  // Stable-identity `allRowsById` — derived only from allRows so it
  // doesn't churn on rowState changes.
  const allRowsById = useMemo(
    () => allRows.reduce((acc, r) => { acc[r.staging_question_id] = r; return acc; }, {}),
    [allRows],
  );
  // Stable callbacks — inline arrow functions inside `rowProps` on every
  // render made the rowProps object unstable, which caused react-window's
  // internal scrollOffset to reset on every Keep/Skip/Defer click (the
  // reported "auto-scrolls to top" bug).
  const handleRowFocus = useCallback((id) => setUrlParam('row', id), [setUrlParam]);
  const handleInboundChipClick = useCallback(
    (targetId) => setUrlParam('inboundTo', targetId),
    [setUrlParam],
  );
  const handleRefreshRow = useCallback(() => fetchSimilarity(true), [fetchSimilarity]);

  // Surgical single-row patch — updates payload.results in-place without
  // re-running the similarity computation. Use this for metadata-only edits
  // (is_decider, etc.) that cannot affect similarity scores.
  const handlePatchRow = useCallback((stagingId, patch) => {
    setPayload(prev => {
      if (!prev?.results?.[stagingId]) return prev;
      return {
        ...prev,
        results: {
          ...prev.results,
          [stagingId]: { ...prev.results[stagingId], ...patch }
        }
      };
    });
  }, []);

  // Memoized rowProps — a stable object reference except when a member
  // meaningfully changed. This is critical: react-window's `List` treats
  // a fresh `rowProps` object as a signal that upstream data mutated and
  // resets its internal scroll offset. Keeping the identity stable
  // preserves the admin's scroll position across triage actions.
  const listRowProps = useMemo(
    () => ({
      rows: filteredRows,
      rowState,
      setAction: setActionPreservingScroll,
      focusedRowId,
      onFocus: handleRowFocus,
      topics,
      internalSurveyors,
      externalSurveyors,
      onRefresh: handleRefreshRow,
      onPatchRow: handlePatchRow,
      token,
      selectedIds,
      onToggleSelect: toggleSelect,
      chainHintByRow,
      inboundByRow,
      allRowsById,
      jumpToRow,
      pulsingRowId,
      inboundToId,
      onClickInboundChip: handleInboundChipClick,
    }),
    [
      filteredRows, rowState, setActionPreservingScroll, focusedRowId, handleRowFocus,
      topics, internalSurveyors, externalSurveyors, handleRefreshRow, handlePatchRow, token,
      selectedIds, toggleSelect, chainHintByRow, inboundByRow, allRowsById,
      jumpToRow, pulsingRowId, inboundToId, handleInboundChipClick,
    ],
  );

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 mr-2" />
        <span className="text-sm text-gray-500">Computing similarities…</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50">
      {/* iter 109: cooperative review lock — blocked-gate modal and
          read-only / stolen banners. Rendered at the top of the tree so
          the modal overlays and the banner sits above the toolbar. */}
      <BatchReviewLockGate
        status={lock.status}
        activeLock={lock.activeLock}
        onEnterReadOnly={lock.enterReadOnly}
        onTakeOver={() => setConfirmTakeOver(true)}
      />
      {confirmTakeOver && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="batch-lock-takeover-confirm"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900">Take over this batch?</h2>
            <p className="mt-1 text-sm text-gray-600">
              {(lock.activeLock?.user_name || 'The current reviewer')}'s
              session will end. Any decisions they haven't Applied yet
              will remain in their browser and won't reach the server.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmTakeOver(false)}>
                Cancel
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => {
                  setConfirmTakeOver(false);
                  lock.acquire(true);
                }}
                data-testid="batch-lock-takeover-confirm-btn"
              >
                Take over
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* === STICKY TOP TOOLBAR === */}
      <div className="bg-white border-b sticky top-0 z-10 px-4 py-2 space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/bulk-import-review')}
            data-testid="back-to-staging">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Zap className="w-5 h-5 text-amber-600" />
          <div className="flex flex-col">
            <div className="font-semibold text-sm flex items-center gap-2">
              Bulk Similarity Triage
              <StagingActionsHelpPopover testId="page-help-btn" align="start" />
              {payload?.elapsed_ms && (
                <Badge variant="outline" className="text-[10px]">{payload.elapsed_ms}ms</Badge>
              )}
            </div>
            {batchScope && (
              <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
                <MiddleTruncate value={batchScope.filename} tailChars={12} className="max-w-[280px]" />
                <span>·</span>
                <span>{batchScope.grade}</span>
                <span>·</span>
                <span>{batchScope.mmd_location}</span>
                <span>·</span>
                <span>{batchScope.function_name}</span>
                <span>·</span>
                <span>{batchScope.questionCount} questions</span>
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="relative">
              <Input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search questions…"
                className="h-8 text-xs w-56 pl-7"
                data-testid="search-input"
              />
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none">⌕</span>
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xs"
                  aria-label="Clear search"
                  data-testid="search-clear-btn"
                >×</button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="threshold-input" className="text-xs text-gray-600">Threshold</Label>
              <Input
                id="threshold-input"
                type="number"
                min={0}
                max={100}
                step={1}
                value={thresholdDraft}
                onChange={(e) => {
                  // Draft-only: does NOT trigger a recompute. Keeps the
                  // raw input so backspace / partial typing stays fluid.
                  setThresholdDraft(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Commit draft → URL → fetch effect re-runs.
                    const raw = parseInt(thresholdDraft, 10);
                    const v = Number.isNaN(raw) ? 70 : Math.max(0, Math.min(100, raw));
                    setUrlParam('threshold', String(v));
                  }
                }}
                onBlur={() => {
                  // Snap the draft back to the last-applied value if the
                  // user typed something invalid then clicked away. Does
                  // NOT commit — that only happens on Recompute or Enter.
                  const raw = parseInt(thresholdDraft, 10);
                  if (Number.isNaN(raw) || raw < 0 || raw > 100) {
                    setThresholdDraft(String(threshold));
                  }
                }}
                className="w-20 h-8 text-xs"
                data-testid="threshold-input"
              />
              <span className="text-xs text-gray-500">%</span>
              <Button variant="outline" size="sm"
                onClick={() => {
                  // Commit the draft to the URL param. The [batchId,
                  // threshold, ...] useEffect then re-runs the fetch.
                  // If the draft equals the current threshold, still
                  // call fetchSimilarity() explicitly so the button
                  // behaves like a "force refresh".
                  const raw = parseInt(thresholdDraft, 10);
                  const v = Number.isNaN(raw) ? 70 : Math.max(0, Math.min(100, raw));
                  if (v === threshold) {
                    fetchSimilarity();
                  } else {
                    setUrlParam('threshold', String(v));
                  }
                }}
                data-testid="recompute-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Recompute
              </Button>
            </div>
            <div className="flex items-center gap-1 border-l pl-3">
              <span className="text-[10px] text-gray-500 mr-1">Match in:</span>
              {['both', 'approved', 'staging'].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setUrlParam('scope', opt === 'both' ? null : opt)}
                  className={`px-2 py-0.5 rounded text-[10px] border ${simScope === opt ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`scope-${opt}-btn`}
                  title={opt === 'both' ? 'Match in both staging + approved DB' : opt === 'approved' ? 'Only approved DB matches' : 'Only staging matches'}
                >
                  {opt === 'both' ? 'Both' : opt === 'approved' ? 'Approved' : 'Staging'}
                </button>
              ))}
            </div>
            {/* Visible-cap dropdown — trims each bucket (approved + staging)
                to the chosen count. Applies client-side over the fixed
                top_k=20 API response, so switching is instant and a
                selected merge target below the cap auto-pins into view. */}
            <div className="flex items-center gap-1 border-l pl-3">
              <span className="text-[10px] text-gray-500 mr-1" title="Applies to each bucket independently (approved + staging). Selected merge targets are auto-pinned even if they fall below the cap.">
                Show
              </span>
              <select
                value={visibleCap}
                onChange={(e) => setVisibleCap(parseInt(e.target.value, 10))}
                className="text-[10px] border border-gray-300 rounded px-1.5 py-0.5 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                data-testid="visible-cap-select"
                aria-label="Visible matches per bucket"
              >
                {VISIBLE_CAP_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n} / bucket</option>
                ))}
              </select>
            </div>
            {/* Actionable-only toggle — hide rows with zero matches at threshold */}
            <div className="flex items-center gap-1 border-l pl-3">
              <button
                onClick={toggleActionableOnly}
                className={`px-2 py-0.5 rounded text-[10px] border flex items-center gap-1 ${showActionableOnly ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                data-testid="actionable-only-toggle"
                title={showActionableOnly
                  ? 'Showing only rows with ≥1 match — click to show ALL rows'
                  : 'Hide rows that have no matches in the current scope (already-unique rows)'}
              >
                <Eye className="w-3 h-3" />
                {showActionableOnly ? 'Actionable only' : 'Show all rows'}
              </button>
              {/* Surveyor-mismatch toggle — shows rows whose CSV surveyor
                  name couldn't be resolved and were fallback-assigned to
                  Unknown Internal / External at import time. */}
              {(() => {
                const mismatchCount = allRows.filter((r) => r.surveyor_mismatch === true).length;
                if (mismatchCount === 0) return null;
                return (
                  <button
                    onClick={toggleMismatchOnly}
                    className={`px-2 py-0.5 rounded text-[10px] border flex items-center gap-1 ${showMismatchOnly ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'}`}
                    data-testid="surveyor-mismatch-toggle"
                    title={showMismatchOnly
                      ? 'Showing only Surveyor Mismatch rows — click to show ALL rows'
                      : `Show only rows whose CSV surveyor name didn't match any known surveyor (${mismatchCount} such row${mismatchCount === 1 ? '' : 's'})`}
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Surveyor Mismatch ({mismatchCount})
                  </button>
                );
              })()}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="legend-btn" title="Colour legend">
                  <Palette className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 text-xs" align="end">
                <div className="font-semibold text-sm mb-2">Colour legend</div>

                {/* Row background — reflects your decision on that row */}
                <div className="text-[11px] font-semibold text-gray-700 mt-1 mb-1">Row background (your decision)</div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border bg-white border-gray-200" />
                    <span><span className="font-medium">Keep</span> — promote to approved DB on Apply</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border bg-emerald-50/60 border-emerald-200" />
                    <span><span className="font-medium">Merge</span> — merge into selected target</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border bg-red-50/60 border-red-200" />
                    <span><span className="font-medium">Delete</span> — remove on Apply</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border bg-amber-50/60 border-amber-200" />
                    <span><span className="font-medium">Defer</span> — no-op, decide later (default)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border bg-indigo-50/60 border-indigo-200" />
                    <span>Picked in <span className="font-medium">bulk-select</span> mode</span>
                  </div>
                </div>

                {/* Row ring — transient overlay states */}
                <div className="text-[11px] font-semibold text-gray-700 mt-3 mb-1">Row ring (transient state)</div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded-full ring-2 ring-amber-400 ring-offset-1 animate-pulse bg-white" />
                    <span>Pulsing yellow — <span className="font-medium">jump target</span>, you just navigated here</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded-full ring-2 ring-blue-400 ring-offset-1 bg-white" />
                    <span>Blue — <span className="font-medium">currently focused</span> (last clicked)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded-full ring-2 ring-indigo-300 ring-offset-1 bg-white" />
                    <span>Indigo — <span className="font-medium">bulk-selected</span></span>
                  </div>
                </div>

                {/* Match badge — similarity strength */}
                <div className="text-[11px] font-semibold text-gray-700 mt-3 mb-1">Match badge (% strength)</div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-1.5 rounded border bg-red-50 text-red-700 border-red-200 text-[10px]">90+</span>
                    <span>Very likely duplicate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-1.5 rounded border bg-amber-50 text-amber-700 border-amber-200 text-[10px]">75–89</span>
                    <span>Likely — worth a look</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-1.5 rounded border bg-gray-50 text-gray-600 border-gray-200 text-[10px]">70–74</span>
                    <span>Borderline — inspect before merging</span>
                  </div>
                </div>

                {/* Match card special state — added in the visible-cap change */}
                <div className="text-[11px] font-semibold text-gray-700 mt-3 mb-1">Match card</div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border bg-emerald-50 border-emerald-500" />
                    <span>Selected merge target</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border bg-emerald-50/40 border-emerald-200" />
                    <span>Pinned target — outside current per-bucket cap</span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="shortcuts-btn">
                  <Keyboard className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 text-xs" align="end">
                <div className="font-semibold text-sm mb-2">Keyboard shortcuts</div>
                <table className="w-full text-[11px]">
                  <tbody>
                    <tr><td className="font-mono pr-2">K</td><td>Keep focused row</td></tr>
                    <tr><td className="font-mono pr-2">M</td><td>Merge with top match</td></tr>
                    <tr><td className="font-mono pr-2">S</td><td>Delete focused row</td></tr>
                    <tr><td className="font-mono pr-2">D</td><td>Defer focused row</td></tr>
                    <tr><td className="font-mono pr-2">J / ↓</td><td>Next row</td></tr>
                    <tr><td className="font-mono pr-2">↑</td><td>Previous row</td></tr>
                  </tbody>
                </table>
                <div className="text-[10px] text-gray-500 mt-2 border-t pt-1">
                  Inputs and selects skip shortcuts automatically.
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Keyword filter — adaptive:
            • ≤ 6 keywords: flat chip row (simple case, no need for a popover).
            • > 6 keywords: compact "Keywords (N)" trigger opens a Popover
              containing a Command (type-to-search) multi-select list.
              Selected keywords always render as removable chips OUTSIDE
              the popover so admins can see + one-click-remove without
              reopening. AND/OR toggle behaviour unchanged. */}
        {allKeywords.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge
              variant={keywordFilters.length === 0 ? 'default' : 'outline'}
              className="cursor-pointer text-[10px]"
              onClick={clearKeywordFilters}
              data-testid="kw-all-btn"
            >
              All ({allRows.length})
            </Badge>

            {allKeywords.length <= 6 ? (
              // Flat chip row for small batches
              allKeywords.map(([kw, n]) => {
                const selected = keywordFilters.includes(kw);
                return (
                  <Badge
                    key={kw}
                    variant={selected ? 'default' : 'outline'}
                    className={`cursor-pointer text-[10px] ${selected ? '' : 'hover:bg-gray-100'}`}
                    onClick={() => toggleKeyword(kw)}
                    data-testid={`kw-${kw}-btn`}
                  >
                    {kw} · {n}
                    {selected && <span className="ml-1 opacity-70">×</span>}
                  </Badge>
                );
              })
            ) : (
              // Popover + Command combobox for medium/large batches
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-[10px] text-gray-700"
                      data-testid="kw-picker-trigger"
                    >
                      <Tag className="w-3 h-3" />
                      Keywords {keywordFilters.length > 0 && `(${keywordFilters.length})`}
                      <ChevronDown className="w-3 h-3 opacity-60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-64" align="start">
                    <Command>
                      <CommandInput
                        placeholder="Search keywords…"
                        className="text-xs h-8"
                        data-testid="kw-picker-search"
                      />
                      <CommandList>
                        <CommandEmpty className="py-4 text-center text-xs text-gray-400">
                          No keywords match.
                        </CommandEmpty>
                        {allKeywords.map(([kw, n]) => {
                          const selected = keywordFilters.includes(kw);
                          return (
                            <CommandItem
                              key={kw}
                              value={kw}
                              onSelect={() => toggleKeyword(kw)}
                              className="text-xs cursor-pointer"
                              data-testid={`kw-picker-item-${kw}`}
                            >
                              <Check
                                className={`mr-2 h-3.5 w-3.5 ${selected ? 'opacity-100 text-indigo-600' : 'opacity-0'}`}
                              />
                              <span className="flex-1 truncate">{kw}</span>
                              <span className="ml-2 text-[10px] text-gray-400">{n}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandList>
                      {keywordFilters.length > 0 && (
                        <div className="border-t px-2 py-1.5 flex items-center justify-between">
                          <span className="text-[10px] text-gray-500">
                            {keywordFilters.length} selected
                          </span>
                          <button
                            onClick={clearKeywordFilters}
                            className="text-[10px] text-gray-500 hover:text-red-600 underline"
                            data-testid="kw-picker-clear"
                          >
                            Clear all
                          </button>
                        </div>
                      )}
                    </Command>
                  </PopoverContent>
                </Popover>

                {/* Selected keywords rendered as removable chips outside
                    the popover, so admins see + can one-click-remove
                    without reopening the picker. */}
                {keywordFilters.map((kw) => {
                  const entry = allKeywords.find(([k]) => k === kw);
                  const n = entry ? entry[1] : 0;
                  return (
                    <Badge
                      key={kw}
                      variant="default"
                      className="cursor-pointer text-[10px]"
                      onClick={() => toggleKeyword(kw)}
                      data-testid={`kw-selected-chip-${kw}`}
                      title="Click to remove"
                    >
                      {kw} · {n}
                      <span className="ml-1 opacity-70">×</span>
                    </Badge>
                  );
                })}
              </>
            )}

            {keywordFilters.length > 1 && (
              <button
                onClick={toggleFilterMode}
                className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50 ml-0.5"
                title="Toggle keyword match mode"
                data-testid="kw-mode-toggle"
              >
                {filterMode === 'or' ? 'OR' : 'AND'}
              </button>
            )}
            {keywordFilters.length > 1 && (
              <span className="text-[10px] text-gray-500 ml-1">
                · {filterMode === 'or' ? 'match ANY' : 'match ALL'} · showing {filteredRows.length} of {allRows.length}
              </span>
            )}
          </div>
        )}

        {/* Active inbound-merge filter banner */}
        {inboundToId && inboundByRow[inboundToId] && (
          <div className="flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="inbound-filter-banner">
            <span className="font-medium text-emerald-900 flex items-center gap-1">
              <GitMerge className="w-3 h-3" />
              Inbound filter:
            </span>
            <span className="text-emerald-800">
              Showing only the {inboundByRow[inboundToId].count} row{inboundByRow[inboundToId].count !== 1 ? 's' : ''} that merge into this target (+ the target itself).
            </span>
            <button
              onClick={() => setUrlParam('inboundTo', null)}
              className="ml-auto text-[10px] text-emerald-700 underline hover:text-emerald-900"
              data-testid="clear-inbound-filter-btn"
            >
              Clear filter ×
            </button>
          </div>
        )}

        {/* Power-Ups */}
        {batchScope && (
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="text-indigo-900 font-medium flex items-center gap-1">
              <Settings2 className="w-3.5 h-3.5" /> Power-Ups
              <span className="text-indigo-700/70 font-normal">
                · acts on {targetIds.length} {targetMode === 'selection' ? 'selected' : 'filtered'} row(s)
              </span>
            </span>
            <PowerUpTopics topics={topics} form={topicForm} setForm={setTopicForm} count={targetIds.length}
              busy={powerupBusy || readOnly} onApply={() => callBulkEdit({ topic_ids: topicForm.topic_ids, topic_mode: topicForm.mode }, () => setTopicForm({ ..._defaults.topic, mode: topicForm.mode }))} />
            <PowerUpSurveyor surveyorForm={surveyorForm} setSurveyorForm={setSurveyorForm}
              internalSurveyors={internalSurveyors} externalSurveyors={externalSurveyors}
              count={targetIds.length} busy={powerupBusy || readOnly}
              onApply={() => {
                if (!surveyorForm.surveyor_id) { toast.error('Pick a surveyor first'); return; }
                const body = { asked_by_internal: surveyorForm.asked_by_internal };
                if (surveyorForm.asked_by_internal) body.surveyor_internal_id = surveyorForm.surveyor_id;
                else body.surveyor_external_id = surveyorForm.surveyor_id;
                callBulkEdit(body, () => setSurveyorForm(_defaults.surveyor));
              }}
            />
            <PowerUpSitting sittingForm={sittingForm} setSittingForm={setSittingForm}
              internalSurveyors={internalSurveyors} externalSurveyors={externalSurveyors}
              count={targetIds.length} busy={powerupBusy || readOnly}
              onApply={() => {
                if (!sittingForm.surveyor_id) { toast.error('Pick a surveyor first'); return; }
                const entry = {
                  surveyor_id: sittingForm.surveyor_id,
                  surveyor_type: sittingForm.asked_by_internal ? 'internal' : 'external',
                  exam_month: `${sittingForm.exam_month} ${sittingForm.exam_year}`,
                  asked: Math.max(1, parseInt(sittingForm.asked, 10) || 1),
                };
                callBulkEdit({ asked_history_entry: entry }, () => setSittingForm(_defaults.sitting));
              }}
            />
            <PowerUpCount countForm={countForm} setCountForm={setCountForm} count={targetIds.length}
              busy={powerupBusy || readOnly}
              onApply={() => {
                const n = parseInt(countForm.asked, 10);
                if (Number.isNaN(n) || n < 0) { toast.error('Asked count must be a non-negative integer'); return; }
                callBulkEdit({ asked_count_set: n }, () => setCountForm(_defaults.count));
              }}
            />
          </div>
        )}

        {/* Selection toolbar — appears when ≥1 row is checkbox-selected.
            Shows count, master "select all in current filter" toggle, and
            Keep/Merge/Skip/Defer bulk-action buttons. Merge is disabled
            when N>1 because merge needs a per-row target choice. */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-[11px] bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5">
            <span className="font-medium text-indigo-900">
              {selectedIds.size} selected
            </span>
            <button
              onClick={selectAllFiltered}
              className="text-[10px] text-indigo-700 underline hover:text-indigo-900"
              data-testid="select-all-filtered-btn"
            >
              {filteredRows.every((r) => selectedIds.has(r.staging_question_id)) && filteredRows.length > 0
                ? 'Deselect all in filter'
                : `Select all in current filter (${filteredRows.length})`}
            </button>
            <span className="text-indigo-300">·</span>
            <span className="text-indigo-700/70 text-[10px]">Apply to selection:</span>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-gray-700 border-gray-300"
              onClick={() => bulkSetAction('keep')} data-testid="bulk-action-keep-btn">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Keep
            </Button>
            <Button
              size="sm" variant="outline"
              className="h-6 px-2 text-[10px] text-emerald-700 border-emerald-300"
              disabled={selectedIds.size > 1}
              title={selectedIds.size > 1 ? 'Merge requires a per-row target choice — use the per-row Merge button on a single row.' : undefined}
              onClick={() => {
                // Single-selection merge: pick top match for that row
                const sid = Array.from(selectedIds)[0];
                const row = allRows.find((r) => r.staging_question_id === sid);
                if (!row) return;
                const hasApproved = (row.approved_similar || []).some((c) => c.similarity >= threshold);
                const hasStaging = (row.staging_similar || []).some((m) => m.similarity >= threshold);
                if (!hasApproved && !hasStaging) {
                  toast.error('No match at or above current threshold to merge into.');
                  return;
                }
                const tgt = (row.approved_similar || []).find((c) => c.similarity >= threshold)
                  || (row.staging_similar || []).find((m) => m.similarity >= threshold);
                if (tgt) {
                  setAction(sid, 'merge', tgt.id, tgt.source === 'approved' ? 'approved' : 'staging');
                  toast.success('Marked as Merge with top match');
                }
              }}
              data-testid="bulk-action-merge-btn"
            >
              <GitMerge className="w-3 h-3 mr-1" /> Merge
            </Button>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-red-700 border-red-300"
              onClick={() => bulkSetAction('skip')} data-testid="bulk-action-skip-btn">
              <SkipForward className="w-3 h-3 mr-1" /> Delete
            </Button>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-amber-700 border-amber-300"
              onClick={() => bulkSetAction('defer')} data-testid="bulk-action-defer-btn">
              <Clock className="w-3 h-3 mr-1" /> Defer
            </Button>
            {/* Quick-Keep-and-Apply — fast path for clean batches. Same
                DB outcome as tagging every selected row Keep then
                clicking the page-level Apply, but bundled behind one
                click + confirm for high-volume triage. */}
            <span className="text-indigo-300">·</span>
            <Button
              size="sm"
              className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleBulkApprove}
              disabled={bulkApproving}
              title="Shortcut: promote all selected rows to the live questions DB in one click (equivalent to Keep + Apply)."
              data-testid="bulk-action-approve-btn"
            >
              {bulkApproving
                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                : <Send className="w-3 h-3 mr-1" />}
              Quick Keep + Apply
            </Button>
            <button
              onClick={clearSelection}
              className="ml-auto text-[10px] text-gray-500 hover:text-gray-800"
              data-testid="clear-selection-btn"
            >
              Clear selection ×
            </button>
          </div>
        )}
      </div>

      {/* === EXCEL-LIKE FLAT LIST (virtualized) === */}
      <div className="flex-1 overflow-hidden p-3" data-testid="rows-flat-list">
        {filteredRows.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            {searchQuery
              ? `No rows match "${searchQuery}"`
              : keywordFilters.length > 0
                ? `No rows with ${keywordFilters.length === 1
                    ? `keyword "${keywordFilters[0]}"`
                    : `${filterMode === 'or' ? 'any' : 'all'} of: ${keywordFilters.map((k) => `"${k}"`).join(filterMode === 'or' ? ' OR ' : ' AND ')}`}`
                : 'No rows in this batch'}
          </div>
        ) : (
          <List
            listRef={listRef}
            rowCount={filteredRows.length}
            rowHeight={rowHeight}
            defaultHeight={600}
            style={{ height: '100%', width: '100%' }}
            overscanCount={3}
            rowComponent={VirtualizedRow}
            rowProps={listRowProps}
          />
        )}
      </div>

      {/* === STICKY APPLY BAR === */}
      <div className="bg-white border-t px-4 py-3 flex items-center justify-between shrink-0">
        <div className="text-xs text-gray-600">
          <div className="flex items-center flex-wrap gap-x-2">
            <span><span className="text-gray-700 font-medium">{summary.keep}</span> Keep</span>
            <span className="text-gray-300">·</span>
            <span><span className="text-emerald-700 font-medium">{summary.merge}</span> Merge</span>
            <span className="text-gray-300">·</span>
            <span><span className="text-red-700 font-medium">{summary.skip}</span> Delete</span>
            <span className="text-gray-300">·</span>
            <span><span className="text-amber-700 font-medium">{summary.defer}</span> Defer</span>
            <span className="text-gray-400">· Only Defer leaves the DB unchanged</span>
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5 flex items-start gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
            <span>
              <strong className="text-gray-600">Apply</strong> commits three actions:
              {' '}<strong className="text-gray-600">Keep</strong> promotes the staging row to the live questions DB,
              {' '}<strong className="text-gray-600">Merge</strong> folds it into a target row (adds a new{' '}
              <code className="text-[10px] bg-emerald-50 px-0.5">asked_by_history</code> entry, then deletes the source),
              and <strong className="text-gray-600">Delete</strong> soft-deletes the staging row (lands in Trash for 7 days, restorable).
              {' '}<strong className="text-gray-600">Defer</strong> is a bookmark — the row stays in triage and Apply skips it entirely.
              Metadata edits (question text, surveyor, topics, month, asked, decider) save instantly — no Apply needed.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/bulk-import-review">
            <Button variant="outline" disabled={applying} data-testid="page-cancel-btn">Cancel</Button>
          </Link>
          <Button
            onClick={handleApply}
            disabled={applying || readOnly || (summary.skip + summary.merge + summary.keep === 0)}
            title={readOnly ? 'Read-only mode — take over the batch to apply changes' : undefined}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="page-apply-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Apply ({summary.skip + summary.merge + summary.keep})
          </Button>
        </div>
      </div>

      {/* === MERGE PREVIEW MODAL === */}
      {applyPreview && (
        <MergePreviewModal
          preview={applyPreview}
          rowState={rowState}
          allRows={allRows}
          onCancel={() => setApplyPreview(null)}
          onConfirm={() => executeApply(applyPreview.skipIds, applyPreview.keepIds, applyPreview.analysis)}
        />
      )}

      {/* === APPLY REPORT MODAL === opens after executeApply, shows per-op
           outcomes with retry + audit-log deep-link. See runApplyPlan.js. */}
      {applyReport && (
        <ApplyReportModal
          report={applyReport}
          onClose={closeApplyReport}
          onRetry={retryFailedOps}
          retrying={retrying}
          onViewAudit={(bId) =>
            navigate(
              bId
                ? `/admin/bulk-import-audit?batch_id=${encodeURIComponent(bId)}`
                : '/admin/bulk-import-audit',
            )
          }
        />
      )}
    </div>
  );
};

// ============================================================================
// VirtualizedRow — wraps ExcelRow with the styling + click handlers that
// previously lived inline in the page. Receives `style` from react-window for
// absolute positioning inside the virtualized viewport.
// ============================================================================
const VirtualizedRow = ({ index, style, ariaAttributes, rows, rowState, setAction, focusedRowId, onFocus, topics, internalSurveyors, externalSurveyors, onRefresh, onPatchRow, token, selectedIds, onToggleSelect, chainHintByRow, inboundByRow, allRowsById, jumpToRow, pulsingRowId, inboundToId, onClickInboundChip }) => {
  const r = rows[index];
  if (!r) return <div style={style} {...ariaAttributes} />;
  const isFocused = focusedRowId === r.staging_question_id;
  const isSelected = selectedIds?.has(r.staging_question_id);
  const isPulsing = pulsingRowId === r.staging_question_id;
  const a = rowState[r.staging_question_id]?.action || 'defer';
  // Pulse takes visual priority while active so admins notice the jump target.
  const ring = isPulsing
    ? 'ring-4 ring-amber-400 ring-offset-1 animate-pulse'
    : isFocused
      ? 'ring-2 ring-blue-400 ring-offset-1'
      : isSelected
        ? 'ring-2 ring-indigo-300 ring-offset-1'
        : '';
  const bg = a === 'skip' ? 'bg-red-50/40 border-red-200'
    : a === 'merge' ? 'bg-emerald-50/40 border-emerald-200'
    : a === 'defer' ? 'bg-amber-50/40 border-amber-200'
    : isSelected ? 'bg-indigo-50/50 border-indigo-200'
    : 'bg-white border-gray-200';
  return (
    <div style={style} {...ariaAttributes}>
      {/* px-1 pt-0.5 ensures the focused row's ring-2 + ring-offset-1 isn't
          clipped by react-window's overflow:hidden on the slot wrapper. */}
      <div className="px-1 pt-0.5">
        <div
          className={`mb-2 border rounded-lg p-3 ${bg} ${ring} transition-shadow`}
          onClick={() => onFocus(r.staging_question_id)}
          data-testid={`flat-row-${r.staging_question_id}`}
        >
          <ExcelRow
            row={r}
            rowState={rowState[r.staging_question_id]}
            setAction={(action, mergeTargetId, mergeTargetSource) =>
              setAction(r.staging_question_id, action, mergeTargetId, mergeTargetSource)}
            topics={topics}
            internalSurveyors={internalSurveyors}
            externalSurveyors={externalSurveyors}
            onRefresh={onRefresh}
            onPatchRow={onPatchRow}
            token={token}
            isSelected={isSelected}
            onToggleSelect={(e) => {
              e.stopPropagation();
              const mode = e.shiftKey ? 'range' : (e.metaKey || e.ctrlKey) ? 'multi' : 'single';
              onToggleSelect?.(r.staging_question_id, index, mode);
            }}
            chainHint={chainHintByRow?.[r.staging_question_id]}
            inboundInfo={inboundByRow?.[r.staging_question_id]}
            allRowsById={allRowsById}
            jumpToRow={jumpToRow}
            isInboundFiltered={inboundToId === r.staging_question_id}
            onClickInboundChip={onClickInboundChip}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ExcelRow — inline per-row card (replaces master-detail; everything visible)
// ============================================================================






const ExcelRow = ({ row, rowState, setAction, topics, internalSurveyors, externalSurveyors, onRefresh, onPatchRow, token, isSelected, onToggleSelect, chainHint, inboundInfo, allRowsById, jumpToRow, isInboundFiltered, onClickInboundChip }) => {
  const action = rowState?.action || 'defer';
  const hasApproved = row.approved_similar_filtered.length > 0;
  // ALL matches are mergeable (backend gates by scope). Used to gate the
  // per-row Merge button enabled state.
  const hasStagingMatch = row.staging_similar_filtered.length > 0;
  const mergeDisabled = !rowState?.mergeTargetId && !hasApproved && !hasStagingMatch;
  const totalMatches = row.approved_similar_filtered.length + row.staging_similar_filtered.length;

  return (
    <div className="grid grid-cols-12 gap-3">
      {/* LEFT — question + metadata */}
      <div className="col-span-5 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {/* Multi-select checkbox — Shift+click selects range, Ctrl/Cmd+click
              toggles individually, plain click toggles + resets range anchor. */}
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={() => { /* handled via onClick to capture modifier keys */ }}
            onClick={onToggleSelect}
            className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            title="Click = select only this row · Ctrl/Cmd+click = toggle · Shift+click = range from anchor"
            data-testid={`row-checkbox-${row.staging_question_id}`}
          />
          <span className="text-[10px] text-gray-400 font-mono">Row {row.row_number}</span>
          <Badge variant="outline" className="text-[9px]">
            {totalMatches} match{totalMatches !== 1 ? 'es' : ''}
          </Badge>
          {/* Inline chain/cycle hint — visible only when this row participates
              in a transitive merge or cycle. Updated live as admin picks targets. */}
          {chainHint && chainHint.kind === 'chain' && (
            <ChainLandingChip hint={chainHint} allRowsById={allRowsById} stagingId={row.staging_question_id} jumpToRow={jumpToRow} />
          )}
          {chainHint && chainHint.kind === 'cycle' && (
            <Badge
              variant="outline"
              className="text-[9px] bg-red-50 text-red-700 border-red-300 flex items-center gap-1"
              title="This row is part of a merge cycle. Fix one of the rows in the cycle before Apply."
              data-testid={`chain-cycle-chip-${row.staging_question_id}`}
            >
              <AlertTriangle className="w-2.5 h-2.5" />
              Cycle
            </Badge>
          )}
          {/* Inbound back-reference chip — appears on TARGET rows; click
              filters the page to show only the inbound sources + this row. */}
          {inboundInfo && inboundInfo.count > 0 && (
            <InboundMergeChip
              count={inboundInfo.count}
              stagingId={row.staging_question_id}
              isActive={isInboundFiltered}
              onClick={onClickInboundChip}
            />
          )}
        </div>
        <EditableQuestionText row={row} onRefresh={onRefresh} token={token} />

        {/* Metadata strip — surveyor / asked / month / decider (all editable via popovers) */}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-600" onClick={(e) => e.stopPropagation()}>
          <EditSurveyorPill row={row} internalSurveyors={internalSurveyors} externalSurveyors={externalSurveyors} onPatchRow={onPatchRow} token={token} />
          <EditAskedPill row={row} onPatchRow={onPatchRow} token={token} />
          <EditMonthPill row={row} onPatchRow={onPatchRow} token={token} />
          <EditDeciderPill row={row} onPatchRow={onPatchRow} token={token} />
        </div>

        {/* Topics strip — clickable to assign/edit topic_ids */}
        <div className="mt-1.5 flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
          <EditTopicsPill row={row} topics={topics} onPatchRow={onPatchRow} token={token} />
        </div>

        {row.keywords && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            <span className="text-[10px] text-gray-400">Keywords:</span>
            {row.keywords.split(';').map((t) => t.trim()).filter(Boolean).map((t) => (
              <Badge key={t} variant="outline" className="text-[9px] text-indigo-700 border-indigo-200 bg-indigo-50">
                {t}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* MIDDLE — matches */}
      <div className="col-span-5 space-y-1.5 max-h-64 overflow-y-auto pr-1">
        {row.approved_similar_filtered.length === 0 && row.staging_similar_filtered.length === 0 && (
          <div className="text-[10px] text-gray-400 italic py-1">No matches at this threshold</div>
        )}
        {row.approved_similar_filtered.map((m) => (
          <MatchCard key={`a-${m.id}`} m={m} rowState={rowState} setAction={setAction} jumpToRow={jumpToRow} />
        ))}
        {row.staging_similar_filtered.map((m) => (
          <MatchCard key={`s-${m.id}`} m={m} rowState={rowState} setAction={setAction} jumpToRow={jumpToRow} />
        ))}
      </div>

      {/* RIGHT — actions */}
      <div className="col-span-2 flex flex-col gap-1">
        {[
          { v: 'keep', icon: CheckCircle2, label: 'Keep', cls: 'text-gray-700' },
          { v: 'merge', icon: GitMerge, label: 'Merge', cls: 'text-emerald-700' },
          { v: 'skip', icon: SkipForward, label: 'Delete', cls: 'text-red-700' },
          { v: 'defer', icon: Clock, label: 'Defer', cls: 'text-amber-700' },
        ].map(({ v, icon: Icon, label, cls }) => {
          const disabled = v === 'merge' && mergeDisabled;
          return (
            <Button
              key={v}
              size="sm"
              variant={action === v ? 'default' : 'outline'}
              className={`h-7 px-2 text-[11px] justify-start ${action === v ? '' : cls}`}
              // Prevent the button from stealing focus on mouse click.
              // Without this, browsers scroll the button into view on
              // focus — which triggered the "list auto-scrolls to top /
              // to the button" bug when the row was near the viewport
              // edge. Keyboard users still get focus via Tab because
              // preventDefault only fires on mousedown.
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                setAction(v, v === 'merge' ? rowState?.mergeTargetId : null, v === 'merge' ? rowState?.mergeTargetSource : null);
              }}
              disabled={disabled}
              title={disabled ? 'Merge needs an approved DB match or a cross-file staging match at this threshold.' : undefined}
              data-testid={`row-action-${v}-${row.staging_question_id}`}
            >
              <Icon className="w-3 h-3 mr-1" /> {label}
            </Button>
          );
        })}
        {action === 'merge' && !rowState?.mergeTargetId && (
          <div className="text-[10px] text-amber-700 mt-1 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
            <span>Pick a match</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Editable metadata pills — each opens a popover that PATCHes one staging row
// via /admin/bulk-import/staging/bulk-edit (single-id) then calls onRefresh
// ============================================================================

// EditableQuestionText — click-to-edit textarea wired to PUT
// /admin/bulk-import/staging/{id} (full-record update endpoint that already
// supports question_text). On save we always trigger onRefresh so similarity
// scores recompute against the edited text.
const EditableQuestionText = ({ row, onRefresh, token }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(row.question_text || '');
  const textareaRef = useRef(null);

  // Reset draft when row data changes (after refresh) and we're not actively editing.
  useEffect(() => {
    if (!editing) setDraft(row.question_text || '');
  }, [row.question_text, editing]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [editing]);

  const cancel = (e) => {
    e?.stopPropagation();
    setDraft(row.question_text || '');
    setEditing(false);
  };

  const save = async (e) => {
    e?.stopPropagation();
    const trimmed = (draft || '').trim();
    if (!trimmed) return toast.error('Question cannot be empty');
    if (trimmed === (row.question_text || '').trim()) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/bulk-import/staging/${row.staging_question_id}`,
        { question_text: trimmed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Question updated — recomputing matches');
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update question');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="group relative cursor-text"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        title="Click to edit question text"
        data-testid={`edit-question-trigger-${row.staging_question_id}`}
      >
        <div className="text-sm text-gray-900 whitespace-pre-wrap pr-5">
          {row.question_text}
        </div>
        <Pencil className="w-3 h-3 text-gray-300 group-hover:text-indigo-500 absolute right-0 top-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()} className="space-y-1.5">
      <Textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') cancel(e);
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(e);
        }}
        rows={Math.min(8, Math.max(3, (draft.match(/\n/g) || []).length + 2))}
        className="text-sm font-mono resize-y border-indigo-300 focus:border-indigo-500 focus-visible:ring-indigo-200"
        disabled={busy}
        data-testid={`edit-question-textarea-${row.staging_question_id}`}
      />
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-400">
          {busy ? 'Saving…' : `${draft.length} chars · ⌘/Ctrl+Enter to save · Esc to cancel`}
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px] text-gray-600 hover:text-gray-900"
            onClick={cancel}
            disabled={busy}
            data-testid={`edit-question-cancel-${row.staging_question_id}`}
          >
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
          <Button
            size="sm"
            className="h-6 px-2 text-[11px] bg-indigo-600 hover:bg-indigo-700"
            onClick={save}
            disabled={busy}
            data-testid={`edit-question-save-${row.staging_question_id}`}
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

const _editSingle = async (stagingId, body, token) => {
  await axios.post(`${API}/admin/bulk-import/staging/bulk-edit`,
    { staging_ids: [stagingId], ...body },
    { headers: { Authorization: `Bearer ${token}` } });
};

const EditSurveyorPill = ({ row, internalSurveyors, externalSurveyors, onPatchRow, token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isInt, setIsInt] = useState(!!row.asked_by_internal);
  const [surveyorId, setSurveyorId] = useState('');
  // Re-sync form state when the pill reopens (row may have been patched since last open)
  useEffect(() => { setIsInt(!!row.asked_by_internal); setSurveyorId(''); }, [row.asked_by_internal, open]);
  const pool = isInt ? internalSurveyors : externalSurveyors;
  const apply = async () => {
    if (!surveyorId) return toast.error('Pick a surveyor');
    setBusy(true);
    try {
      const body = { asked_by_internal: isInt };
      if (isInt) body.surveyor_internal_id = surveyorId;
      else body.surveyor_external_id = surveyorId;
      await _editSingle(row.staging_question_id, body, token);
      const surveyorName = pool.find(s => s.id === surveyorId)?.name || '';
      onPatchRow?.(row.staging_question_id, {
        asked_by_internal: isInt,
        surveyor_name: surveyorName,
        surveyor_mismatch: false,
      });
      toast.success('Surveyor updated');
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSurveyorId(''); }}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-gray-100 cursor-pointer" data-testid={`edit-surveyor-${row.staging_question_id}`}>
          {row.surveyor_name ? (
            <>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${row.asked_by_internal ? 'bg-blue-500' : 'bg-purple-500'}`} />
              <span
                className={`font-medium ${row.surveyor_mismatch ? 'text-amber-700 italic' : 'text-gray-700'}`}
                title={row.surveyor_mismatch
                  ? `Surveyor Mismatch — CSV said "${row.surveyor_raw_input || '(blank)'}" but no matching surveyor was found. Row was fallback-assigned to ${row.surveyor_name}. Click to reassign.`
                  : undefined}
              >
                {row.surveyor_name}
              </span>
              <span className="text-gray-400">
                ({row.asked_by_internal ? 'Int' : 'Ext'}{row.surveyor_mismatch ? ' · mismatch' : ''})
              </span>
            </>
          ) : (
            <span className="text-gray-400 italic">No surveyor</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="start">
        <div className="space-y-2">
          <Label className="text-xs">Surveyor type</Label>
          <Select value={isInt ? 'internal' : 'external'} onValueChange={(v) => { setIsInt(v === 'internal'); setSurveyorId(''); }}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="external">External</SelectItem>
              <SelectItem value="internal">Internal</SelectItem>
            </SelectContent>
          </Select>
          <Label className="text-xs">Surveyor</Label>
          <Select value={surveyorId} onValueChange={setSurveyorId}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick a surveyor…" /></SelectTrigger>
            <SelectContent className="max-h-60">
              {pool.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700" onClick={apply} disabled={busy || !surveyorId}>
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const EditAskedPill = ({ row, onPatchRow, token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [val, setVal] = useState(row.asked ?? 1);
  useEffect(() => { setVal(row.asked ?? 1); }, [row.asked, open]);
  const apply = async () => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n) || n < 0) return toast.error('Asked must be ≥ 0');
    setBusy(true);
    try {
      await _editSingle(row.staging_question_id, { asked_count_set: n }, token);
      onPatchRow?.(row.staging_question_id, { asked: n });
      toast.success('Asked count updated');
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="px-1.5 py-0.5 rounded hover:bg-gray-100 cursor-pointer" data-testid={`edit-asked-${row.staging_question_id}`}>
          <span className="text-gray-400">Asked:</span>{' '}
          <span className="font-medium text-gray-700">{row.asked ?? 1}</span>
          {row.asked_by_history_count > 0 && (
            <span className="text-gray-400"> · {row.asked_by_history_count} sitting{row.asked_by_history_count !== 1 ? 's' : ''}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56" align="start">
        <div className="space-y-2">
          <Label className="text-xs">Asked count (overrides total)</Label>
          <div className="text-[10px] text-gray-500">Does not touch asked_by_history.</div>
          <Input type="number" min={0} value={val} onChange={(e) => setVal(e.target.value)} className="h-8 text-xs" />
          <Button size="sm" className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700" onClick={apply} disabled={busy}>
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const EditMonthPill = ({ row, onPatchRow, token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const parsed = (() => {
    const m = (row.exam_month || '').match(/^(\w+)\s+(\d{4})$/);
    return m ? { month: m[1], year: m[2] } : { month: MONTHS[new Date().getMonth()], year: String(new Date().getFullYear()) };
  })();
  const [month, setMonth] = useState(parsed.month);
  const [year, setYear] = useState(parsed.year);
  useEffect(() => { setMonth(parsed.month); setYear(parsed.year); }, [row.exam_month, open, parsed.month, parsed.year]);
  const apply = async () => {
    setBusy(true);
    try {
      const newMonth = `${month} ${year}`;
      await _editSingle(row.staging_question_id, { exam_month: newMonth }, token);
      onPatchRow?.(row.staging_question_id, { exam_month: newMonth });
      toast.success('Month updated');
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="px-1.5 py-0.5 rounded hover:bg-gray-100 cursor-pointer" data-testid={`edit-month-${row.staging_question_id}`}>
          {row.exam_month ? (
            <><span className="text-gray-400">Month:</span> <span className="font-medium text-gray-700">{row.exam_month}</span></>
          ) : (
            <span className="text-gray-400 italic">No month</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56" align="start">
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-1">
            <div className="col-span-2">
              <Label className="text-xs">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Year</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="h-8 text-xs" />
            </div>
          </div>
          <Button size="sm" className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700" onClick={apply} disabled={busy}>
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const EditDeciderPill = ({ row, onPatchRow, token }) => {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    const next = !row.is_decider;
    setBusy(true);
    try {
      await _editSingle(row.staging_question_id, { is_decider: next }, token);
      onPatchRow?.(row.staging_question_id, { is_decider: next }); // instant confirmed patch
      toast.success(`Decider ${next ? 'set' : 'cleared'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <button onClick={toggle} disabled={busy}
      className={`px-1.5 py-0.5 rounded text-[9px] border ${row.is_decider ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-gray-50 text-gray-400 border-gray-200 hover:text-rose-600 hover:border-rose-200'}`}
      title={row.is_decider ? 'Decider — click to unset' : 'Not decider — click to mark'}
      data-testid={`edit-decider-${row.staging_question_id}`}>
      {busy ? '…' : (row.is_decider ? 'DECIDER ✓' : 'decider')}
    </button>
  );
};

const EditTopicsPill = ({ row, topics, onPatchRow, token }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(row.topic_ids || []);
  useEffect(() => { setSelected(row.topic_ids || []); }, [row.topic_ids, open]);

  // Build hierarchy: depth + formatted "Parent › Child" path for each topic
  const { byId, depthOf, pathOf, sortedTopics } = useMemo(() => {
    const map = Object.fromEntries(topics.map((t) => [t.id, t]));
    const depth = (id) => {
      let d = 0; let cur = map[id];
      while (cur?.parent_topic_id && map[cur.parent_topic_id]) { d++; cur = map[cur.parent_topic_id]; }
      return d;
    };
    const path = (id) => {
      const parts = []; let cur = map[id];
      while (cur) { parts.unshift(cur.name); cur = cur.parent_topic_id ? map[cur.parent_topic_id] : null; }
      return parts.join(' › ');
    };
    // Sort so parents come before children (by full path)
    const sorted = [...topics].sort((a, b) => path(a.id).localeCompare(path(b.id)));
    return { byId: map, depthOf: depth, pathOf: path, sortedTopics: sorted };
  }, [topics]);

  const apply = async () => {
    setBusy(true);
    try {
      await _editSingle(row.staging_question_id, { topic_ids: selected }, token);
      onPatchRow?.(row.staging_question_id, { topic_ids: selected });
      toast.success('Topics updated');
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  // Render the assigned chips as full "Parent › Child" paths (use byId lookup
  // so freshly-edited rows reflect hierarchy without waiting for backend refresh).
  const assignedChips = (row.topic_ids || [])
    .map((id) => ({ id, label: byId[id] ? pathOf(id) : (row.topic_names?.[(row.topic_ids || []).indexOf(id)] || 'Unknown') }));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-1 flex-wrap px-1.5 py-0.5 rounded hover:bg-gray-100 cursor-pointer text-left" data-testid={`edit-topics-${row.staging_question_id}`}>
          <span className="text-[10px] text-gray-400">Topics:</span>
          {assignedChips.length > 0
            ? assignedChips.map(({ id, label }) => (
                <Badge key={id} variant="outline" className="text-[9px] text-emerald-700 border-emerald-200 bg-emerald-50">
                  {label}
                </Badge>
              ))
            : <Badge variant="outline" className="text-[9px] text-gray-400 border-gray-200 bg-gray-50">unassigned</Badge>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[440px] p-3 overflow-visible"
        align="start"
        side="bottom"
        sideOffset={4}
        collisionPadding={16}
      >
        <div className="space-y-2">
          <Label className="text-xs">Topics ({topics.length}) — multi-select</Label>
          <div className="text-[10px] text-gray-500">Empty selection clears topics. Type to search across all levels.</div>
          <TopicPickerMulti
            topics={topics}
            value={selected}
            onChange={setSelected}
            placeholder="Assign topics…"
            testIdPrefix="triage-row-topic"
          />
          <div className="flex justify-between pt-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => setSelected([])}>Clear all</Button>
            <Button size="sm" className="h-7 px-3 text-[11px] bg-indigo-600 hover:bg-indigo-700" onClick={apply} disabled={busy}>
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};


// ============================================================================
// FocusedRowDetail — kept for the old master-detail layout (unused now, but
// retained so the file can be split into modules later without churn)
// ============================================================================
const FocusedRowDetail = ({ row, rowState, setAction }) => {
  const action = rowState?.action || 'defer';
  const hasApproved = row.approved_similar_filtered.length > 0;
  // ALL matches are mergeable (backend gates by scope). Used to gate the
  // per-row Merge button enabled state.
  const hasStagingMatch = row.staging_similar_filtered.length > 0;
  const mergeDisabled = !rowState?.mergeTargetId && !hasApproved && !hasStagingMatch;

  return (
    <div className="space-y-4">
      {/* Question header */}
      <div>
        <div className="text-[10px] text-gray-400 font-mono mb-1">Row {row.row_number}</div>
        <div className="text-base text-gray-900 whitespace-pre-wrap">{row.question_text}</div>
        {row.keywords && (
          <div className="flex flex-wrap gap-1 mt-2">
            {row.keywords.split(';').map((t) => t.trim()).filter(Boolean).map((t) => (
              <Badge key={t} variant="outline" className="text-[10px] text-indigo-700 border-indigo-200 bg-indigo-50">
                {t}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border rounded-lg p-3 bg-gray-50">
        <div className="text-xs text-gray-500 mb-2">Decide</div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { v: 'keep', icon: CheckCircle2, label: 'Keep', shortcut: 'K' },
            { v: 'merge', icon: GitMerge, label: 'Merge', shortcut: 'M' },
            { v: 'skip', icon: SkipForward, label: 'Delete', shortcut: 'S' },
            { v: 'defer', icon: Clock, label: 'Defer', shortcut: 'D' },
          ].map(({ v, icon: Icon, label, shortcut }) => {
            const disabled = v === 'merge' && mergeDisabled;
            return (
              <Button
                key={v}
                variant={action === v ? 'default' : 'outline'}
                size="sm"
                disabled={disabled}
                title={disabled ? 'Merge needs an approved DB match or cross-file staging match at this threshold.' : undefined}
                className={`text-xs ${action === v ? '' : v === 'merge' ? 'text-emerald-700' : v === 'skip' ? 'text-red-700' : v === 'defer' ? 'text-amber-700' : 'text-gray-700'}`}
                onClick={() => setAction(v, v === 'merge' ? rowState?.mergeTargetId : null, v === 'merge' ? rowState?.mergeTargetSource : null)}
                data-testid={`focused-action-${v}`}
              >
                <Icon className="w-3.5 h-3.5 mr-1" /> {label}
                <kbd className="ml-1 text-[9px] text-gray-400">{shortcut}</kbd>
              </Button>
            );
          })}
        </div>
        {action === 'merge' && !rowState?.mergeTargetId && (
          <div className="text-[10px] text-amber-700 mt-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Pick a match below to set the merge target.
          </div>
        )}
      </div>

      {/* Approved matches */}
      <div>
        <div className="text-xs font-semibold text-gray-700 mb-1.5">
          Approved DB matches ({row.approved_similar_filtered.length})
        </div>
        {row.approved_similar_filtered.length === 0 ? (
          <div className="text-[11px] text-gray-400 italic">None at this threshold</div>
        ) : (
          <div className="space-y-1.5">
            {row.approved_similar_filtered.map((m) => (
              <MatchCard key={`a-${m.id}`} m={m} rowState={rowState} setAction={setAction} />
            ))}
          </div>
        )}
      </div>

      {/* Staging matches */}
      <div>
        <div className="text-xs font-semibold text-gray-700 mb-1.5">
          Staging matches ({row.staging_similar_filtered.length})
          <span className="text-[10px] text-gray-400 ml-1 font-normal">
            same-batch shown for awareness only; cross-file can be merged
          </span>
        </div>
        {row.staging_similar_filtered.length === 0 ? (
          <div className="text-[11px] text-gray-400 italic">None at this threshold</div>
        ) : (
          <div className="space-y-1.5">
            {row.staging_similar_filtered.map((m) => (
              <MatchCard key={`s-${m.id}`} m={m} rowState={rowState} setAction={setAction} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Match Card — reused for both approved + staging matches
// ============================================================================
const MatchCard = ({ m, rowState, setAction, jumpToRow }) => {
  // ALL staging matches are mergeable — the staging→staging merge endpoint
  // accepts any pair within the same scope (grade + MMD + function) and
  // consolidates their sittings into the target row's asked_by_history.
  // This includes:
  //   • cross-batch staging matches (e.g. dup.csv in batch A → batch B)
  //   • cross-file matches within the same batch
  //   • same-file duplicates (the core dup.csv use case — admin merges
  //     row 2 into row 4 to consolidate two sittings into one record)
  // Approved matches are also always mergeable. Scope mismatches are
  // already filtered out server-side (only matches in same scope appear).
  const isMergeable = m.source === 'approved' || m.source === 'staging';
  const targetSource = m.source === 'approved' ? 'approved' : 'staging';
  const isSelected = rowState?.action === 'merge' && rowState?.mergeTargetId === m.id;
  const isPinned = !!m._pinned;
  return (
    <div className={`p-2 border rounded text-xs ${isSelected ? 'border-emerald-500 bg-emerald-50' : isPinned ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
      data-testid={`match-${m.id}`}>
      {isPinned && (
        <div className="mb-1 text-[9px] text-emerald-700 flex items-center gap-1" data-testid={`pinned-badge-${m.id}`}>
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Pinned merge target · ranked #{m._rank} · outside current cap
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <span className="text-gray-800 line-clamp-2 flex-1">{m.question_text}</span>
        <div className="flex items-center gap-1 shrink-0">
          <Popover>
            <PopoverTrigger asChild>
              <button className="p-0.5 text-gray-400 hover:text-indigo-600 rounded"
                title="Preview full question & sittings"
                data-testid={`preview-${m.id}`}
                aria-label="Preview">
                <Eye className="w-3.5 h-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] text-xs" align="end" side="left">
              <MatchPreview m={m} />
            </PopoverContent>
          </Popover>
          <Badge variant="outline" className={
            m.similarity >= 90 ? 'bg-red-50 text-red-700 border-red-200'
              : m.similarity >= 75 ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-gray-50 text-gray-600'
          }>
            {m.similarity}%
          </Badge>
        </div>
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500">
        <span>
          {m.source === 'approved' ? (
            <>Approved · {m.surveyor_name} · {m.exam_month || '—'}</>
          ) : (
            <>
              Staging · {m.source_filename || '—'}
              {m.is_same_batch && ' (this batch)'}
              {m.row_number != null && (
                <>
                  {' '}—{' '}
                  {m.is_same_batch ? (
                    // Same-batch: virtualization-safe scroll via existing
                    // jumpToRow → sets ?row= URL param + brief highlight.
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); jumpToRow && jumpToRow(m.id); }}
                      className="text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                      title="Scroll to this row on the current page"
                      data-testid={`jump-to-same-batch-${m.id}`}
                    >
                      Row {m.row_number}
                    </button>
                  ) : (
                    // Cross-batch: open the other batch's triage page in a
                    // new tab with the row pre-highlighted via ?row= param
                    // (BulkSimilarityPage already respects that param on mount).
                    <a
                      href={`/admin/bulk-import-review/batch/${m.import_batch_id}?row=${m.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                      title="Open that batch in a new tab"
                      data-testid={`jump-to-cross-batch-${m.id}`}
                    >
                      Row {m.row_number} ↗
                    </a>
                  )}
                </>
              )}
            </>
          )}
        </span>
        {isMergeable && (
          <button
            onClick={() => setAction('merge', m.id, targetSource)}
            className={`text-[10px] hover:underline ${targetSource === 'staging' ? 'text-indigo-700' : 'text-emerald-700'}`}
            data-testid={`pick-${m.id}`}
          >
            {isSelected ? '✓ Merge target' : targetSource === 'staging' ? 'Merge into this' : 'Merge with existing'}
          </button>
        )}
      </div>
    </div>
  );
};

const MatchPreview = ({ m }) => (
  <div className="space-y-2">
    <div className="flex items-start justify-between gap-2">
      <div className="font-semibold text-sm text-gray-800">
        {m.source === 'approved' ? 'Approved DB question' : 'Staging question'}
      </div>
      <Badge variant="outline" className={`shrink-0 ${m.similarity >= 90 ? 'bg-red-50 text-red-700 border-red-200' : m.similarity >= 75 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-600'}`}>
        {m.similarity}% match
      </Badge>
    </div>
    <div className="border rounded p-2 bg-gray-50 text-gray-800 max-h-32 overflow-y-auto whitespace-pre-wrap">
      {m.question_text}
    </div>
    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
      <div><span className="text-gray-500">Grade:</span> <span className="font-medium">{m.grade || '—'}</span></div>
      <div><span className="text-gray-500">MMD:</span> <span className="font-medium">{m.mmd_location || '—'}</span></div>
      <div className="col-span-2"><span className="text-gray-500">Function:</span> <span className="font-medium">{m.function_name || '—'}</span></div>
      <div><span className="text-gray-500">Surveyor:</span> <span className="font-medium">{m.surveyor_name || '—'}</span></div>
      <div><span className="text-gray-500">Exam:</span> <span className="font-medium">{m.exam_month || '—'}</span></div>
      <div><span className="text-gray-500">Asked:</span> <span className="font-medium">{m.asked ?? '—'}</span></div>
      <div><span className="text-gray-500">Sittings:</span> <span className="font-medium">{m.history_count ?? 0}</span></div>
    </div>
    {m.keywords && (
      <div>
        <div className="text-[10px] text-gray-500 mb-0.5">Keywords</div>
        <div className="flex flex-wrap gap-1">
          {String(m.keywords).split(';').map((t) => t.trim()).filter(Boolean).map((t) => (
            <Badge key={t} variant="outline" className="text-[9px] text-indigo-700 border-indigo-200 bg-indigo-50">{t}</Badge>
          ))}
        </div>
      </div>
    )}
    {m.history_recent && m.history_recent.length > 0 && (
      <div>
        <div className="text-[10px] text-gray-500 mb-1">
          Recent sittings {m.history_count > m.history_recent.length ? `(${m.history_recent.length} of ${m.history_count})` : ''}
        </div>
        <ul className="space-y-0.5 text-[11px]">
          {m.history_recent.map((h, i) => (
            <li key={i} className="flex items-center gap-2 text-gray-700">
              <span className="text-gray-400">•</span>
              <span className="font-medium">{h.surveyor_name || '—'}</span>
              <span className="text-gray-500">·</span>
              <span>{h.exam_month || '—'}</span>
              {h.asked > 1 && <span className="text-gray-400">×{h.asked}</span>}
            </li>
          ))}
        </ul>
      </div>
    )}
    {m.source === 'staging' && (
      <div className="text-[10px] text-gray-400 pt-1 border-t">
        From {m.source_filename || '—'} · Q{m.question_number || '?'} · Row {m.row_number}{m.is_same_batch && ' (this batch)'}
      </div>
    )}
  </div>
);

// ============================================================================
// Power-Up popovers — extracted for readability
// ============================================================================
const PowerUpTopics = ({ topics, form, setForm, count, busy, onApply }) => {
  // Same hierarchy ordering + indentation as the per-row EditTopicsPill
  const { depthOf, sortedTopics } = useMemo(() => {
    const map = Object.fromEntries(topics.map((t) => [t.id, t]));
    const depth = (id) => {
      let d = 0; let cur = map[id];
      while (cur?.parent_topic_id && map[cur.parent_topic_id]) { d++; cur = map[cur.parent_topic_id]; }
      return d;
    };
    const path = (id) => {
      const parts = []; let cur = map[id];
      while (cur) { parts.unshift(cur.name); cur = cur.parent_topic_id ? map[cur.parent_topic_id] : null; }
      return parts.join(' › ');
    };
    const sorted = [...topics].sort((a, b) => path(a.id).localeCompare(path(b.id)));
    return { depthOf: depth, sortedTopics: sorted };
  }, [topics]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy || count === 0}
          data-testid="pu-topics-btn">
          <Tag className="w-3 h-3 mr-1" /> Set Topics
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[440px] p-3 overflow-visible"
        align="end"
        side="bottom"
        sideOffset={4}
        collisionPadding={16}
      >
        <div className="space-y-2">
          {/* Mode selector — Add (default) is safest for bulk operations
              because it can't silently wipe prior curation. Replace kept
              for the "standardize a group" use case. Remove is the
              recovery lever for an accidental bulk-add. */}
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Mode</Label>
            <div className="flex rounded border border-gray-300 overflow-hidden text-[10px]">
              {[
                { v: 'add',     label: 'Add',     hint: 'Union with each row\'s existing topics (ancestors pruned)' },
                { v: 'replace', label: 'Replace', hint: 'Overwrite each row\'s topics with exactly this list' },
                { v: 'remove',  label: 'Remove',  hint: 'Subtract these topics from each row\'s existing set' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => {
                    setForm({ ...form, mode: opt.v });
                    try { localStorage.setItem('bulk-topic-mode-v1', opt.v); } catch { /* quota */ }
                  }}
                  className={`px-2 py-0.5 border-r last:border-r-0 border-gray-300 transition-colors ${
                    form.mode === opt.v
                      ? (opt.v === 'replace' ? 'bg-amber-500 text-white' : opt.v === 'remove' ? 'bg-rose-500 text-white' : 'bg-emerald-600 text-white')
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  title={opt.hint}
                  data-testid={`pu-topic-mode-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <Label className="text-xs">Topics ({topics.length}) — applies to {count} filtered row(s)</Label>
          <div className="text-[10px] text-gray-500">
            {form.mode === 'add' && 'Selected topics will be added to each row\'s existing set. Ancestor topics are auto-pruned when a descendant is present.'}
            {form.mode === 'replace' && 'Each row\'s existing topics will be overwritten. Empty selection clears topics.'}
            {form.mode === 'remove' && 'Selected topics will be removed from each row\'s existing set (rows without them are untouched).'}
          </div>
          <TopicPickerMulti
            topics={topics}
            value={form.topic_ids}
            onChange={(ids) => setForm({ ...form, topic_ids: ids })}
            placeholder={form.mode === 'remove' ? 'Topics to remove…' : 'Assign topics…'}
            testIdPrefix="pu-topic"
          />
          <div className="flex justify-between pt-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]"
              onClick={() => setForm({ ...form, topic_ids: [] })}>Clear all</Button>
            <Button size="sm" className={`h-7 px-3 text-[11px] ${
              form.mode === 'replace' ? 'bg-amber-600 hover:bg-amber-700' :
              form.mode === 'remove'  ? 'bg-rose-600 hover:bg-rose-700' :
                                         'bg-indigo-600 hover:bg-indigo-700'
            }`}
              onClick={onApply} disabled={busy || (form.mode !== 'replace' && form.topic_ids.length === 0)}
              data-testid="pu-topics-apply">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              {form.mode === 'add' && `Add to ${count}`}
              {form.mode === 'replace' && `Replace on ${count}`}
              {form.mode === 'remove' && `Remove from ${count}`}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const PowerUpSurveyor = ({ surveyorForm, setSurveyorForm, internalSurveyors, externalSurveyors, count, busy, onApply }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy || count === 0}
        data-testid="pu-surveyor-btn">
        <UserCheck className="w-3 h-3 mr-1" /> Change Surveyor
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-72" align="end">
      <div className="space-y-2">
        <Label className="text-xs">Type</Label>
        <Select value={surveyorForm.asked_by_internal ? 'internal' : 'external'}
          onValueChange={(v) => setSurveyorForm({ asked_by_internal: v === 'internal', surveyor_id: '' })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="external">External</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
          </SelectContent>
        </Select>
        <Label className="text-xs">Surveyor</Label>
        <Select value={surveyorForm.surveyor_id || ''} onValueChange={(v) => setSurveyorForm((p) => ({ ...p, surveyor_id: v }))}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick a surveyor..." /></SelectTrigger>
          <SelectContent className="max-h-72">
            {(surveyorForm.asked_by_internal ? internalSurveyors : externalSurveyors).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
          onClick={onApply} disabled={busy || !surveyorForm.surveyor_id} data-testid="pu-surveyor-apply">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
          Apply to {count}
        </Button>
      </div>
    </PopoverContent>
  </Popover>
);

const PowerUpSitting = ({ sittingForm, setSittingForm, internalSurveyors, externalSurveyors, count, busy, onApply }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy || count === 0}
        data-testid="pu-sitting-btn">
        <Plus className="w-3 h-3 mr-1" /> Add Sitting
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-72" align="end">
      <div className="space-y-2">
        <div className="text-[10px] text-gray-500">Appends an asked_by_history entry; bumps asked count by N.</div>
        <Label className="text-xs">Type</Label>
        <Select value={sittingForm.asked_by_internal ? 'internal' : 'external'}
          onValueChange={(v) => setSittingForm((p) => ({ ...p, asked_by_internal: v === 'internal', surveyor_id: '' }))}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="external">External</SelectItem>
            <SelectItem value="internal">Internal</SelectItem>
          </SelectContent>
        </Select>
        <Label className="text-xs">Surveyor</Label>
        <Select value={sittingForm.surveyor_id || ''} onValueChange={(v) => setSittingForm((p) => ({ ...p, surveyor_id: v }))}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick a surveyor..." /></SelectTrigger>
          <SelectContent className="max-h-60">
            {(sittingForm.asked_by_internal ? internalSurveyors : externalSurveyors).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-3 gap-1">
          <div className="col-span-2">
            <Label className="text-xs">Month</Label>
            <Select value={sittingForm.exam_month} onValueChange={(v) => setSittingForm((p) => ({ ...p, exam_month: v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Year</Label>
            <Input type="number" value={sittingForm.exam_year}
              onChange={(e) => setSittingForm((p) => ({ ...p, exam_year: e.target.value }))} className="h-8 text-xs" />
          </div>
        </div>
        <Label className="text-xs">Count to add</Label>
        <Input type="number" min={1} value={sittingForm.asked}
          onChange={(e) => setSittingForm((p) => ({ ...p, asked: e.target.value }))} className="h-8 text-xs" />
        <Button size="sm" className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
          onClick={onApply} disabled={busy || !sittingForm.surveyor_id} data-testid="pu-sitting-apply">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
          Append to {count}
        </Button>
      </div>
    </PopoverContent>
  </Popover>
);

const PowerUpCount = ({ countForm, setCountForm, count, busy, onApply }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy || count === 0}
        data-testid="pu-count-btn">
        <Settings2 className="w-3 h-3 mr-1" /> Set Asked Count
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-64" align="end">
      <div className="space-y-2">
        <Label className="text-xs">New asked count (overrides existing)</Label>
        <div className="text-[10px] text-gray-500">Does not touch asked_by_history.</div>
        <Input type="number" min={0} value={countForm.asked}
          onChange={(e) => setCountForm({ asked: e.target.value })} className="h-8 text-xs" />
        <Button size="sm" className="w-full h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
          onClick={onApply} disabled={busy} data-testid="pu-count-apply">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
          Apply to {count}
        </Button>
      </div>
    </PopoverContent>
  </Popover>
);

export default BulkSimilarityPage;
